<template>
  <v-container :class="{ 'force-tablet': forceTabletLayout }" style="position: relative;">
    <!-- Create New Tournament Section -->
    <div v-if="!tournamentActive" style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px;">
      <h1>Welcome to Tournament Timer</h1>
      <p v-if="!isProduction" class="text-caption text-medium-emphasis mb-0">Using the dev DB</p>
      <p style="margin-bottom: 24px; text-align: center; max-width: 500px;">
        Create a new tournament to get started. You can choose from your saved tournament structures or create a new one.
      </p>
      <v-btn 
        color="primary" 
        size="large" 
        @click="openCreateDialog"
        prepend-icon="mdi-plus"
      >
        Create New Tournament
      </v-btn>
      
      <v-btn 
        color="secondary" 
        size="large" 
        :to="{ name: 'SettingsView' }"
        prepend-icon="mdi-cog"
        style="margin-top: 16px;"
      >
        Manage Tournament Structures
      </v-btn>

      <v-btn 
        color="secondary" 
        size="large" 
        :to="{ name: 'TournamentResultsList' }"
        prepend-icon="mdi-trophy"
        variant="outlined"
        style="margin-top: 16px;"
      >
        View tournament results
      </v-btn>
      <v-btn
        color="secondary"
        size="large"
        :to="{ name: 'LinkedPlayersResults' }"
        prepend-icon="mdi-account-multiple"
        variant="outlined"
        style="margin-top: 16px;"
      >
        Linked players results
      </v-btn>
      <v-btn
        color="secondary"
        size="large"
        :to="{ name: 'TournamentViewReadOnly' }"
        prepend-icon="mdi-eye"
        variant="outlined"
        style="margin-top: 16px;"
      >
        Watch a tournament
      </v-btn>
      
      <!-- Feedback Message -->
      <div v-if="feedback" style="margin-top: 16px; padding: 12px; border-radius: 4px; text-align: center; max-width: 500px;"
           :style="{ 
             backgroundColor: feedback.includes('Error') ? '#ffebee' : '#e8f5e8',
             color: feedback.includes('Error') ? '#c62828' : '#2e7d32',
             border: feedback.includes('Error') ? '1px solid #ffcdd2' : '1px solid #c8e6c9'
           }">
        {{ feedback }}
      </div>
    </div>

    <!-- Tournament Content (only shown when tournament is active) -->
    <div v-if="tournamentActive">
      <!-- Navigation Buttons -->
      <div class="tournament-nav-buttons" style="position: absolute; top: 8px; right: 8px; z-index: 10; display: flex; gap: 8px;">
        <v-btn
          v-if="trackPlayersByName"
          icon
          @click="navigateToPlayersView"
        >
          <v-icon>mdi-account-group</v-icon>
        </v-btn>
        <v-btn icon :to="{ name: 'SettingsView' }">
          <v-icon>mdi-cog</v-icon>
        </v-btn>
        <v-btn icon color="warning" @click="showEndTournamentDialog = true" title="End Tournament">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <!-- Wake Lock indicator -->
        <v-btn
          icon
          :title="wakeLockActive ? 'Screen wake lock active' : 'Screen wake lock inactive or unsupported'"
          :color="wakeLockActive ? 'success' : 'grey'">
          <v-icon>{{ wakeLockActive ? 'mdi-lock' : 'mdi-lock-open-variant' }}</v-icon>
        </v-btn>
        <!-- Admin-only toggle to force tablet layout preview -->
        <v-btn
          v-if="userStore.admin"
          icon
          :title="forceTabletLayout ? 'Disable Tablet Preview (admin)' : 'Enable Tablet Preview (admin)'"
          :color="forceTabletLayout ? 'primary' : 'grey'"
          @click="forceTabletLayout = !forceTabletLayout">
          <v-icon>{{ forceTabletLayout ? 'mdi-tablet' : 'mdi-tablet-android' }}</v-icon>
        </v-btn>
      </div>
      <h1 style="margin-bottom: 16px;">{{ newTournamentName }}
        <v-btn icon @click="uiStore.toggleNavBar()" :title="uiStore.navBarVisible ? 'Hide Menu' : 'Show Menu'">
          <v-icon>{{ uiStore.navBarVisible ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
        </v-btn>
      </h1>

 

      <!-- Control Buttons -->
      <div class="control-buttons">
        <v-btn v-if="!showPreCountdownScreen && !(timeLeft === countdownDuration && !timerRunning)" color="warning" class="mr-2" @click="resetLevel">Restart Level</v-btn>
        <v-btn v-if="!timerRunning" color="primary" class="mr-2" @click="startTimer"
          :disabled="playersLeft < 2"
          :title="playersLeft < 2 ? 'Need at least 2 players to run the timer' : ''">
          {{ isPreCountdown ? 'Resume Countdown' : isAtStart ? 'Start Tournament' : 'Resume Tournament' }}
        </v-btn>
        <v-btn v-if="timerRunning" color="secondary" class="mr-2" @click="stopTimer">Pause Tournament</v-btn>
        <v-btn v-if="trackPlayersByName && !showPreCountdownScreen" color="error" class="mr-2" @click="bustDialog = true">Bust</v-btn>
        <v-btn color="error" @click="confirmResetAll">Restart Tournament</v-btn>
      </div>

      <!-- Main Display from Shared Component -->
      <TournamentDisplay :is-editable="true" :level="level" :timer-state="timerState" :level-state="levelState"
        :payout-state="payoutState" :summary-state="summaryState" :announcement="announcement" :display-config="displayConfig"
        :rebuy-end-level="rebuyEndLevel"
        @decrement-level="decrementLevel" @increment-level="incrementLevel" @edit-timer="openEditTimerDialog" />

      <div v-if="nextBreakLevel" class="next-break-notice">Next break after Level {{ nextBreakLevel }}</div>
      <!-- Chip Denominations Visualizer -->
      <div v-if="chipDenominations.length > 0" class="chip-display-container" style="display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 16px; margin: 8px auto 24px; padding: 16px; background-color: #f0f0f0; border-radius: 8px; max-width: 800px;">
        <div v-for="(chip, index) in chipDenominations" :key="`vis-${index}`" class="chip-visual" :style="{ backgroundColor: chip.color, borderColor: chip.accentColor }">
          <div class="chip-value" :style="{ color: getContrastingTextColor(chip.color) }">
            {{ chip.value }}
          </div>
        </div>
      </div>

      <!-- Summary from Shared Component -->
      <TournamentSummary :players-left="playersLeft" :total-players="totalPlayers" :total-rebuys="totalRebuys"
        :total-add-ons="totalAddOns" :rebuy-allowed="rebuyAllowed" :addon-allowed="addonAllowed" :track-players-by-name="trackPlayersByName" :busted-player-count="bustedPlayerCount" @adjust-count="adjustPlayerCount" @open-bust-dialog="bustDialog = true" :is-editable="true"
      >
        <template #default>
          <!-- Share ID: only show when a tournament code was set (no way to watch without a code) -->
          <div v-if="shareId" style="margin-top: 24px;">
            <label style="font-weight: bold;">Tournament Code for Watching:</label>
            <div style="margin-top: 4px; padding: 8px; background-color: #f5f5f5; border-radius: 4px; font-family: monospace; font-size: 1.1rem; text-align: center;">
              <strong>{{ shareId }}</strong>
            </div>
            <div style="margin-top: 8px; color: #888; font-size: 0.9em; text-align: center;">
              Share the above code and the link below with others to let them watch the tournament
            </div>
              <div style="margin-top: 12px; display:flex; gap:8px; justify-content:center; align-items:center; flex-direction:column;">
                <a :href="`http://www.27pub.com/vt?code=${shareId}`" target="_blank" rel="noopener" style="font-family:monospace; font-size:0.95rem; color:#1976d2; text-decoration:underline;">
                  http://www.27pub.com/vt
                </a>
              </div>
          </div>
        </template>
      </TournamentSummary>
    </div>

    <!-- Create Tournament Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">Create New Tournament</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="createTournament">
            <v-text-field
              v-model="newTournamentName"
              label="Tournament Name"
              required
              style="margin-bottom: 16px;"
            />
            
            <v-text-field
              v-model="newTournamentAlias"
              label="Tournament Code (optional, for watching)"
              style="margin-bottom: 16px;"
              hint="Optional. If blank, your tournament name will be used for the watch link."
              persistent-hint
            />
            
            <v-select
              v-model="selectedLeagueId"
              :items="availableLeagues"
              item-title="name"
              item-value="id"
              label="League (optional)"
              clearable
              density="compact"
              style="margin-bottom: 16px;"
            />

            <v-select
              v-model="selectedStructureId"
              :items="structureOptions"
              item-title="name"
              item-value="id"
              label="Tournament Structure"
              required
              style="margin-bottom: 16px;"
              @update:model-value="onStructureSelect"
            />
            
            <div v-if="structureOptions.length === 0" style="margin-bottom: 16px; padding: 12px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px;">
              <p style="margin: 0 0 8px 0; color: #856404;">No tournament structures found.</p>
              <v-btn 
                color="primary" 
                size="small" 
                :to="{ name: 'SettingsView' }"
                prepend-icon="mdi-cog"
              >
                Go to Settings to Create Structure
              </v-btn>
            </div>
            
            <div v-if="selectedStructure" style="margin-bottom: 16px;">
              <h4>Structure Details:</h4>
              <div style="font-size: 0.9em; color: #666;">
                <div>Buy-in: ${{ selectedStructure.buyinAmount || 0 }}</div>
                <div v-if="selectedStructure.rebuyAllowed">Rebuy: ${{ selectedStructure.rebuyAmount || 0 }}</div>
                <div v-if="selectedStructure.addonAllowed">Add-on: ${{ selectedStructure.addonAmount || 0 }}</div>
                <div>Level Length: {{ selectedStructure.defaultDuration || 12 }} minutes</div>
                <div>Break Length: {{ selectedStructure.breakDuration || 5 }} minutes</div>
                <div>Total Levels: {{ selectedStructure.levels?.length || 0 }}</div>
              </div>
            </div>
          </v-form>
          <!-- Dialog Feedback Message -->
          <div v-if="createDialogFeedback" style="margin-top: 16px; padding: 12px; border-radius: 4px; text-align: center;"
               :style="{ 
                 backgroundColor: '#ffebee',
                 color: '#c62828',
                 border: '1px solid #ffcdd2'
               }">
            {{ createDialogFeedback }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showCreateDialog = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            text 
            @click="createTournament"
            :disabled="!newTournamentName || !selectedStructureId"
          >
            Create Tournament
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resetAllDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Reset</v-card-title>
        <v-card-text>Are you sure you want to start the tournament over?  <p>Note: This will also reset the players.</p></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelResetAll">Cancel</v-btn>
          <v-btn color="red" text @click="doResetAll">Yes, Reset</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Countdown Timer Dialog -->
    <v-dialog v-model="showEditTimerDialog" max-width="360">
      <v-card>
        <v-card-title>Edit Countdown Timer</v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">
            Level maximum: {{ Math.floor((countdownDuration ?? 0) / 60) }}:{{ String((countdownDuration ?? 0) % 60).padStart(2, '0') }}
          </div>
          <div class="d-flex align-center justify-center ga-2">
            <v-text-field
              v-model.number="editTimerMinutes"
              type="number"
              label="Minutes"
              min="0"
              density="compact"
              hide-details
              style="width: 100px"
            />
            <span class="text-h5 font-weight-bold">:</span>
            <v-text-field
              v-model.number="editTimerSeconds"
              type="number"
              label="Seconds"
              min="0"
              max="59"
              density="compact"
              hide-details
              style="width: 100px"
            />
          </div>
          <div v-if="editTimerError" class="text-error text-caption mt-2">{{ editTimerError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showEditTimerDialog = false">Cancel</v-btn>
          <v-btn color="primary" text :disabled="!!editTimerError" @click="applyEditedTimer">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showEndTournamentDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm End Tournament</v-card-title>
        <v-card-text>Are you sure you want to end the current tournament?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showEndTournamentDialog = false">Cancel</v-btn>
          <v-btn color="warning" text @click="onConfirmEndTournament">Yes, End Tournament</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showOnePlayerLeftDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">One Player Left</v-card-title>
        <v-card-text>Only one player remains. End the tournament?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showOnePlayerLeftDialog = false">Keep Playing</v-btn>
          <v-btn color="warning" text @click="showOnePlayerLeftDialog = false; onConfirmEndTournament()">End Tournament</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSaveResultsDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="headline">Save tournament results?</v-card-title>
        <v-card-text>Would you like to save the results of this tournament (player names, places, winnings, buy-ins, and levels) for your records?</v-card-text>
        <v-card-actions class="flex-wrap ga-2 justify-end">
          <v-btn color="grey" text @click="onDeclineSaveResults">Don't save</v-btn>
          <v-btn color="primary" text @click="onConfirmSaveResults">Save results</v-btn>
          <v-btn color="secondary" text @click="openPayoutEditor">Save with updated payouts</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payout Editor Dialog -->
    <v-dialog v-model="showPayoutEditorDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>Edit Payouts</v-card-title>
        <v-card-text>
          <v-table density="compact">
            <thead>
              <tr>
                <th class="text-center" style="width:60px">Place</th>
                <th>Player</th>
                <th style="width:150px">Payout</th>
                <th style="width:40px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(payout, index) in editablePayouts" :key="index">
                <td class="text-center">{{ payout.position }}</td>
                <td>{{ payout.playerName || '—' }}</td>
                <td>
                  <v-text-field
                    v-model.number="payout.amount"
                    type="number"
                    prefix="$"
                    density="compact"
                    hide-details
                    min="0"
                    variant="underlined"
                  />
                </td>
                <td>
                  <v-btn icon size="x-small" variant="text" color="error" @click="removePayoutPlace(index)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>

          <v-btn variant="text" color="primary" size="small" class="mt-2" @click="addPayoutPlace">
            <v-icon start>mdi-plus</v-icon>Add Place
          </v-btn>

          <v-divider class="my-3" />

          <div class="d-flex justify-space-between text-body-2 px-1">
            <span>Total Pot: <strong>${{ totalPot }}</strong></span>
            <span>Paid Out: <strong>${{ editablePayoutsTotal }}</strong></span>
            <span :class="editablePayoutsRemaining !== 0 ? 'text-error' : 'text-success'">
              Remaining: <strong>{{ editablePayoutsRemaining > 0 ? '+$' + editablePayoutsRemaining : editablePayoutsRemaining < 0 ? '-$' + Math.abs(editablePayoutsRemaining) : '$0' }}</strong>
            </span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showPayoutEditorDialog = false; showSaveResultsDialog = true">Cancel</v-btn>
          <v-btn color="primary" text @click="onPayoutEditorSave">Save Results</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payout Total Mismatch Warning Dialog -->
    <v-dialog v-model="showPayoutWarningDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>Payout Total Mismatch</v-card-title>
        <v-card-text>
          The payouts total <strong>${{ editablePayoutsTotal }}</strong> but the pot is <strong>${{ totalPot }}</strong>.
          <span v-if="editablePayoutsRemaining > 0">
            You are <strong>${{ editablePayoutsRemaining }}</strong> short.
          </span>
          <span v-else>
            You are <strong>${{ Math.abs(editablePayoutsRemaining) }}</strong> over.
          </span>
          Save anyway?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showPayoutWarningDialog = false">Go Back</v-btn>
          <v-btn color="warning" text @click="confirmPayoutEditorSave">Save Anyway</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Select Active Tournament Dialog -->
    <v-dialog v-model="showSelectTournamentDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">Select an Active Tournament</v-card-title>
        <v-card-text>
          <p>You have active tournaments. Please choose one to load, or create a new one.</p>
          <v-list lines="two">
            <v-list-item
              v-for="tourney in activeTournamentsForUser"
              :key="tourney.id"
              @click="loadSelectedTournament(tourney.id)"
              :title="tourney.tournamentName"
              :subtitle="`Code: ${tourney.codeName}`"
            >
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="outlined" @click="handleCreateNewFromSelect">
            Create New Tournament
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Select Active Tournament Dialog -->
    <v-dialog v-model="showSelectTournamentDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">Select an Active Tournament</v-card-title>
        <v-card-text>
          <p>You have active tournaments. Please choose one to load, or create a new one.</p>
          <v-list lines="two">
            <v-list-item
              v-for="tourney in activeTournamentsForUser"
              :key="tourney.id"
              @click="loadSelectedTournament(tourney.id)"
              :title="tourney.tournamentName"
              :subtitle="`Code: ${tourney.codeName}`"
            >
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="outlined" @click="handleCreateNewFromSelect">
            Create New Tournament
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Select Active Tournament Dialog -->
    <v-dialog v-model="showSelectTournamentDialog" max-width="600" persistent origin="center center">
      <v-card>
        <v-card-title class="headline">Select an Active Tournament</v-card-title>
        <v-card-text>
          <p>You have active tournaments. Please choose one to load, or create a new one.</p>
          <v-list lines="two">
            <v-list-item
              v-for="tourney in activeTournamentsForUser"
              :key="tourney.id"
              @click="loadSelectedTournament(tourney.id)"
              :title="tourney.tournamentName"
              :subtitle="`Code: ${tourney.codeName}`"
            >
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="outlined" @click="handleCreateNewFromSelect">
            Create New Tournament
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Quick Bust Dialog -->
    <v-dialog v-model="bustDialog" max-width="500">
      <v-card>
        <v-card-title>Bust Player</v-card-title>
        <v-card-text>
          <v-select
            v-model="bustingPlayerId"
            :items="activePlayers"
            item-title="name"
            item-value="id"
            label="Select player who was busted"
            clearable
            style="margin-bottom: 16px;"
          />
          <template v-if="bustingPlayerId">
            <v-select
              v-model="bustKnockedOutBy"
              :items="bustKnockedOutByOptions"
              label="Knocked out by"
              clearable
              style="margin-bottom: 16px;"
            />
            <v-checkbox
              v-if="rebuyAllowed"
              v-model="bustDidRebuy"
              label="Player chose to rebuy"
            />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelTournamentBust">Cancel</v-btn>
          <v-btn color="primary" text :disabled="!bustingPlayerId" @click="confirmTournamentBust">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rebalance warning after quick bust -->
    <v-dialog v-model="bustRebalanceDialog" max-width="450">
      <v-card>
        <v-card-title class="headline">Rebalance Recommended</v-card-title>
        <v-card-text>
          A table now has significantly fewer players. It is recommended to rebalance the tables to ensure fair play.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="bustRebalanceDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Audio elements for sound alerts -->
    <audio ref="oneMinuteWarningSound" src="/sounds/one_minute_warning.mp3" preload="auto"></audio>
    <audio ref="levelEndSound" src="/sounds/level_end.mp3" preload="auto"></audio>
    <!-- Placeholder: place tournament-start.mp3 at public/sounds/tournament-start.mp3 to enable this sound -->
    <audio ref="tournamentStartSound" src="/sounds/tournament-start.mp3" preload="auto"></audio>
    <!-- Shown only if the browser (iPad Safari) blocked a sound; any tap re-enables it (see useAudioUnlock) -->
    <v-alert v-if="showSilentHint" type="info" variant="tonal" density="compact" closable @click:close="dismissSilentHint" style="position: fixed; bottom: 90px; left: 16px; right: 16px; z-index: 3000;">
      Timer sounds are muted when this iPad is in silent mode. Turn silent mode off (the bell in Control Center) to hear them.
    </v-alert>
    <v-btn v-if="soundBlocked" color="warning" prepend-icon="mdi-volume-high" style="position: fixed; bottom: 90px; right: 16px; z-index: 3000;">Tap to enable sound</v-btn>
  </v-container>
</template>

<script setup>
import { ref, computed, onUnmounted, onMounted, watch, onBeforeUnmount } from 'vue';
import { useUserStore } from '@/stores/users';
import { useTournamentStore } from '@/stores/tournament';
import { db } from '@/firebaseConfig';
import { useUiStore } from '@/stores/ui';
import { doc, getDoc, setDoc, addDoc, onSnapshot, collection, getDocs, query, where, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import TournamentSummary from '@/components/custom/TournamentSummary.vue';
import TournamentDisplay from '@/components/custom/TournamentDisplay.vue';
import { usePayouts } from '@/compostables/usePayouts';
import { useAudioUnlock } from '@/compostables/useAudioUnlock';
import { lookupUserByAlias, getAllowedPlayerOptions } from '@/api/users.js';
import { getLeaguesForRunner } from '@/api/leagues.js';
import {
  TOURNAMENT_TIMERS_DB,
  SAVED_TOURNAMENT_RESULTS_DB,
  TOURNAMENT_SETTINGS_DB,
  TOURNAMENT_PLAYERS_DB,
  TOURNAMENT_PAYOUTS_DB,
  SESSIONS_DB,
  TOURNAMENT,
  NO_LIMIT,
  // eslint-disable-next-line no-unused-vars -- TOURNAMENT_STRUCTURES_DB used in loadStructuresForCreate
  TOURNAMENT_STRUCTURES_DB,
  isProduction
} from '@/constants/constants';
//import { setupCustomDevtools } from '@/plugins/chrome-devtools';
import { useRouter } from 'vue-router';
import { isTableUnbalancedAfterBust } from '@/utils/playerUtils.js';
import { mergeDisplayConfig } from '@/utils/displayConfig';

const router = useRouter();
const userStore = useUserStore();
const tournamentStore = useTournamentStore();
const uiStore = useUiStore();

// Keep local players/bustedPlayers in sync with the Pinia store so that
// changes made in PlayersView are reflected immediately without waiting for
// the Firestore onSnapshot round-trip.
watch(() => tournamentStore.players, (val) => {
  if (Array.isArray(val)) {
    players.value = val;
  }
}, { deep: true });
watch(() => tournamentStore.bustedPlayers, (val) => {
  if (Array.isArray(val)) {
    bustedPlayers.value = val;
  }
}, { deep: true });

const user = computed(() => {
  // Create a user object that has a `uid` property, which is what all the
  // Firestore data fetching functions in this component expect.
  return userStore.userid ? { uid: userStore.userid } : null;
});

// Admin-only layout preview toggle (persisted in localStorage)
const forceTabletLayout = ref(false);
try {
  if (userStore.admin) {
    const stored = localStorage.getItem('forceTabletLayout');
    forceTabletLayout.value = stored === 'true';
  }
} catch (e) {
  console.debug('localStorage unavailable or access denied', e);
}
watch(forceTabletLayout, (val) => {
  try {
    localStorage.setItem('forceTabletLayout', val ? 'true' : 'false');
  } catch (e) {
    console.debug('localStorage setItem failed', e);
  }
});

  // --- Wake Lock management ---
  // Attempt to keep the screen awake while a tournament is active and the timer is running.
  let wakeLock = null;
  const wakeLockActive = ref(false);
  async function requestWakeLock() {
    try {
      if ('wakeLock' in navigator && navigator.wakeLock.request) {
        wakeLock = await navigator.wakeLock.request('screen');
        // Re-acquire on release if still needed
        wakeLock.addEventListener('release', () => {
          console.log('Wake Lock was released');
        });
        wakeLockActive.value = true;
        console.log('Wake Lock acquired');
      } else {
        console.log('Wake Lock API not supported in this browser');
      }
    } catch (err) {
      console.error('Could not obtain Wake Lock:', err);
    }
  }

  async function releaseWakeLock() {
    try {
      if (wakeLock && typeof wakeLock.release === 'function') {
        await wakeLock.release();
        wakeLock = null;
        wakeLockActive.value = false;
        console.log('Wake Lock released manually');
      }
    } catch (err) {
      console.error('Error releasing Wake Lock:', err);
    }
  }

  // Tournament creation state
  const tournamentActive = ref(false);
const showCreateDialog = ref(false);
const showEndTournamentDialog = ref(false);
const showSaveResultsDialog = ref(false);
const newTournamentName = ref('');
const newTournamentAlias = ref('');
const selectedStructureId = ref(null);
const structureOptions = ref([]);
const selectedStructure = ref(null);
const activeTournamentsForUser = ref([]);
const showSelectTournamentDialog = ref(false);
const createDialogFeedback = ref('');
const selectedLeagueId = ref(null);
const availableLeagues = ref([]);

const countdownDuration = ref(null); // in seconds
const timeLeft = ref(null);
const timerRunning = ref(false);

// --- Edit Timer Dialog ---
const showEditTimerDialog = ref(false);
const editTimerMinutes = ref(0);
const editTimerSeconds = ref(0);

const editTimerError = computed(() => {
  const mins = Number(editTimerMinutes.value) || 0;
  const secs = Number(editTimerSeconds.value) || 0;
  if (secs < 0 || secs > 59) return 'Seconds must be between 0 and 59.';
  const total = mins * 60 + secs;
  if (total <= 0) return 'Time must be greater than zero.';
  if (countdownDuration.value !== null && total > countdownDuration.value) {
    const maxMins = Math.floor(countdownDuration.value / 60);
    const maxSecs = countdownDuration.value % 60;
    return `Cannot exceed the level duration of ${maxMins}:${String(maxSecs).padStart(2, '0')}.`;
  }
  return '';
});

function openEditTimerDialog() {
  const t = timeLeft.value ?? 0;
  editTimerMinutes.value = Math.floor(t / 60);
  editTimerSeconds.value = t % 60;
  showEditTimerDialog.value = true;
}

async function applyEditedTimer() {
  if (editTimerError.value) return;
  timeLeft.value = (Number(editTimerMinutes.value) || 0) * 60 + (Number(editTimerSeconds.value) || 0);
  showEditTimerDialog.value = false;
  await saveStateToFirestore();
}
// Watch tournament and timer state to manage Wake Lock
watch([() => tournamentActive.value, () => timerRunning.value], async ([active, running]) => {
  if (active && running) {
    await requestWakeLock();
  } else {
    await releaseWakeLock();
  }
});

const level = ref(1);
const normalLevelCount = ref(1); // Track normal levels separately
const tournamentSecondsElapsed = ref(0); // Derived each tick: elapsedSecondsBeforeCurrentLevel + (countdownDuration - timeLeft)
const elapsedSecondsBeforeCurrentLevel = ref(tournamentStore.timerState.elapsedSecondsBeforeCurrentLevel ?? 0);
let timerInterval = null;
let defaultDurationFromSettings = ref(12); // in minutes
let breakDurationFromSettings = ref(5); // in minutes
const startCountdownSeconds = ref(0);
const betweenRoundsPauseSeconds = ref(0);
const isPreCountdown = ref(false);
const preCountdownTimeLeft = ref(0);
const preCountdownEndTime = ref(null);
const feedback = ref('');
const loading = ref(false);
const levels = ref([]);
const players = ref([]);
const bustedPlayers = ref([]);
const buyinAmount = ref(0);
const rebuyAmount = ref(0);
const addonAmount = ref(0);
const buyinRake = ref(0);
const rebuyRake = ref(0);
const addonRake = ref(0);
const rebuyAllowed = ref(false);
const rebuyEndLevel = ref(null);
const addonAllowed = ref(false);
const buyinChips = ref(1000);
const trackPlayersByName = ref(false);
const addonChips = ref(1000);
const maxTables = ref(0);
const maxPlayersPerTable = ref(10);
const postedAnnouncementText = ref('');
const announcement = computed(() => (postedAnnouncementText.value ? { text: postedAnnouncementText.value } : null));
const displayConfig = ref(mergeDisplayConfig());

const isAtStart = computed(() => {
  return level.value === 1 && timeLeft.value === countdownDuration.value;
});

// True when the pre-countdown screen should be displayed: either the countdown is actively
// ticking OR the tournament hasn't started yet and a countdown is configured.
const showPreCountdownScreen = computed(() => {
  if (!startCountdownSeconds.value) return false;
  if (isPreCountdown.value) return true;
  return level.value === 1
    && elapsedSecondsBeforeCurrentLevel.value === 0
    && timeLeft.value === countdownDuration.value;
});
const chipDenominations = ref([]);
const shareId = ref('');
let playersUnsub = null;
let payoutsUnsub = null;
let settingsUnsub = null;

// Quick-bust dialog state
const bustDialog = ref(false);
const bustingPlayerId = ref(null);
const bustKnockedOutBy = ref('');
const bustDidRebuy = ref(false);
const bustRebalanceDialog = ref(false);

// Manual count refs
const manualTotalPlayers = ref(0);
const manualTotalRebuys = ref(0);
const manualTotalAddOns = ref(0);
const bustedPlayerCount = ref(0);

// Sound refs
const oneMinuteWarningSound = ref(null);
// iPads block timer-driven play() until each element has been played from a tap; unlock on first tap
const { playSound, soundBlocked, showSilentHint, dismissSilentHint } = useAudioUnlock(() => [oneMinuteWarningSound.value, levelEndSound.value, tournamentStartSound.value]);
const levelEndSound = ref(null);
const tournamentStartSound = ref(null);

// Replace single prizesTemplate with multiple payout tables
const payoutTables = ref([
  {
    minEntries: 1,
    maxEntries: 8,
    numberOfPayouts: 3,
    prizesTemplate: [50, 30, 20],
    rounding: 'roundToNearestDollar'
  },
  {
    minEntries: 9,
    maxEntries: 16,
    numberOfPayouts: 4,
    prizesTemplate: [40, 25, 20, 15],
    rounding: 'roundToNearestDollar'
  },
  {
    minEntries: 17,
    maxEntries: 999,
    numberOfPayouts: 5,
    prizesTemplate: [35, 22, 18, 15, 10],
    rounding: 'roundToNearestDollar'
  }
]);

// Add endTime to timer state
const endTime = ref(null); // timestamp in ms

const currentLevelData = computed(() => {
  if (!levels.value || levels.value.length === 0) return null;
  const idx = Math.max(0, Math.min(level.value - 1, levels.value.length - 1));
  return levels.value[idx];
});

const isCurrentLevelBreak = computed(() => {
  return currentLevelData.value?.isBreak || false;
});

// Level number (in the same "Level N" numbering shown on-screen, which skips breaks)
// after which the next break occurs, or null if no break is upcoming.
const nextBreakLevel = computed(() => {
  if (!levels.value || levels.value.length === 0 || isCurrentLevelBreak.value) return null;
  let idx = level.value;
  while (idx < levels.value.length && !levels.value[idx].isBreak) {
    idx++;
  }
  if (idx >= levels.value.length) return null;
  let count = 0;
  for (let i = 0; i < idx; i++) {
    if (!levels.value[i].isBreak) count++;
  }
  return count;
});

const playersLeft = computed(() => {
  if (trackPlayersByName.value) {
    let totalEntries = 0;
    players.value.forEach(player => {
      totalEntries += 1 + (player.rebuys || 0); // Original entry + rebuys
    });
    return totalEntries - bustedPlayers.value.length;
  }
  return (manualTotalPlayers.value + manualTotalRebuys.value) - bustedPlayerCount.value;
});

const totalPlayers = computed(() => {
  return trackPlayersByName.value ? players.value.length : manualTotalPlayers.value;
});

// Pause timer when players left drops below 2
watch([playersLeft, () => timerRunning.value], ([left, running]) => {
  if (running && left < 2) {
    stopTimer();
    setFeedbackMessage('Timer paused: fewer than 2 players in the tournament.');
  }
});

const showOnePlayerLeftDialog = ref(false);

// Prompt to end the tournament when exactly one player remains (from 2+)
watch(playersLeft, (newVal, oldVal) => {
  if (newVal === 1 && oldVal >= 2 && tournamentActive.value) {
    showOnePlayerLeftDialog.value = true;
  }
});

const totalRebuys = computed(() => {
  if (trackPlayersByName.value) {
    return players.value.reduce((total, player) => total + (player.rebuys || 0), 0);
  }
  return manualTotalRebuys.value;
});

const totalAddOns = computed(() => {
  if (trackPlayersByName.value) {
    return players.value.reduce((total, player) => total + (player.addOn ? 1 : 0), 0);
  }
  return manualTotalAddOns.value;
});

const totalPot = computed(() => {
  let pot = totalPlayers.value * (buyinAmount.value - buyinRake.value);
  if (rebuyAllowed.value) {
    pot += totalRebuys.value * (rebuyAmount.value - rebuyRake.value);
  }
  if (addonAllowed.value) {
    pot += totalAddOns.value * (addonAmount.value - addonRake.value);
  }
  return pot;
});

// When the pot changes (e.g. a rebuy is recorded in track-by-name mode), recalculate payouts.
// oldVal guard prevents firing on initial load when totalPot goes from 0 to its starting value.
watch(totalPot, async (newVal, oldVal) => {
  if (!tournamentActive.value || !newTournamentName.value || !user.value?.uid) return;
  if (oldVal === 0 || oldVal == null) return;
  if (newVal === oldVal) return;
  const { recalculateAndSavePayouts } = usePayouts();
  await recalculateAndSavePayouts({
    userId: user.value.uid,
    tournamentId: newTournamentName.value,
    numPlayers: trackPlayersByName.value ? players.value.length : manualTotalPlayers.value,
    numRebuys: trackPlayersByName.value
      ? players.value.reduce((t, p) => t + (p.rebuys || 0), 0)
      : manualTotalRebuys.value,
    totalPot: newVal,
    payoutTables: payoutTables.value
  });
});

const totalChips = computed(() => {
  let chips = totalPlayers.value * buyinChips.value;
  if (rebuyAllowed.value) {
    chips += totalRebuys.value * buyinChips.value; // Rebuys get same chips as buy-in
  }
  if (addonAllowed.value) {
    chips += totalAddOns.value * addonChips.value;
  }
  return chips;
});

const averageChips = computed(() => {
  if (playersLeft.value === 0) return 0;
  return Math.round(totalChips.value / playersLeft.value);
});

const formattedTime = computed(() => {
  if (isPreCountdown.value) {
    const t = preCountdownTimeLeft.value ?? 0;
    const minutes = Math.floor(t / 60).toString().padStart(2, '0');
    const seconds = (t % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }
  if (showPreCountdownScreen.value) {
    // Waiting to start — show the configured countdown duration
    const t = startCountdownSeconds.value;
    const minutes = Math.floor(t / 60).toString().padStart(2, '0');
    const seconds = (t % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }
  if (timeLeft.value === null) return '--:--';
  const minutes = Math.floor(timeLeft.value / 60).toString().padStart(2, '0');
  const seconds = (timeLeft.value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
});

const timerState = computed(() => ({
  formattedTime: formattedTime.value,
  isCurrentLevelBreak: isCurrentLevelBreak.value,
  normalLevelCount: normalLevelCount.value,
  timerRunning: timerRunning.value,
  isPreCountdown: isPreCountdown.value,
  showPreCountdownScreen: showPreCountdownScreen.value,
}));

const levelState = computed(() => ({
  currentLevelData: currentLevelData.value,
  nextLevelData: nextLevelData.value,
}));

const payoutState = computed(() => ({
  visiblePayouts: visiblePayouts.value,
  numberOfPayouts: numberOfPayouts.value,
  totalPrizePool: totalPrizePool.value,
}));

const summaryState = computed(() => ({
  totalPot: totalPot.value,
  averageChips: averageChips.value,
  // Use total entries for display conditions, as it's more accurate than just original players
  totalPlayers: trackPlayersByName.value
    ? totalPlayers.value
    : totalPlayers.value + totalRebuys.value,
  }));

// --- Payouts State ---
const numberOfPayouts = ref(3);
const payouts = ref([]);
const totalPrizePool = computed(() => totalPot.value);

// --- Payout Editor State ---
const showPayoutEditorDialog = ref(false);
const editablePayouts = ref([]);
const showPayoutWarningDialog = ref(false);

const orderedPlayersForEditor = computed(() => {
  if (!players.value) return [];
  const N = players.value.length;
  // A player is busted only if their current-life entry is in bustedPlayers.
  // Players who rebuyed have a bust entry for their original id but are NOT out —
  // only a bust on their rebuy life (id_rebuy_N) means they are eliminated.
  const isCurrentlyBusted = (player) => {
    const rebuys = player.rebuys || 0;
    const currentEntryId = rebuys > 0 ? `${player.id}_rebuy_${rebuys}` : String(player.id);
    return bustedPlayers.value.some(b => String(b.playerId) === currentEntryId);
  };
  const activePlayers = players.value.filter(p => !isCurrentlyBusted(p));
  // Group busts by base player id (strip _rebuy_N), keeping only the latest bust per player.
  const baseId = (playerId) => String(playerId).includes('_rebuy_')
    ? String(playerId).split('_rebuy_')[0]
    : String(playerId);
  const bustedByPlayer = new Map();
  bustedPlayers.value.forEach(b => {
    const key = baseId(b.playerId);
    const current = bustedByPlayer.get(key);
    if (current == null || b.order > current.order) {
      bustedByPlayer.set(key, { order: b.order });
    }
  });
  // Only include players who are actually eliminated (current life is busted)
  const bustedSorted = [...bustedByPlayer.entries()]
    .filter(([id]) => {
      const player = players.value.find(pl => String(pl.id) === id);
      return player ? isCurrentlyBusted(player) : false;
    })
    .sort((a, b) => a[1].order - b[1].order);
  const result = [];
  for (let i = 0; i < activePlayers.length; i++) {
    result.push({ place: i + 1, name: activePlayers[i].name || '' });
  }
  for (let i = 0; i < bustedSorted.length; i++) {
    const [playerId] = bustedSorted[i];
    const p = players.value.find(pl => String(pl.id) === playerId);
    result.push({ place: N - i, name: p?.name || '' });
  }
  result.sort((a, b) => a.place - b.place);
  return result;
});

const editablePayoutsTotal = computed(() =>
  editablePayouts.value.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
);
const editablePayoutsRemaining = computed(() => totalPot.value - editablePayoutsTotal.value);

// --- Payouts Scrolling State ---
const payoutScrollIndex = ref(0);
let payoutScrollInterval = null;
const visiblePayouts = computed(() => {
  if (!payouts.value || payouts.value.length <= 5) return payouts.value;
  // Loop through payouts in groups of 5
  const start = payoutScrollIndex.value;
  const end = start + 5;
  if (end <= payouts.value.length) {
    return payouts.value.slice(start, end);
  } else {
    // Wrap around
    return [
      ...payouts.value.slice(start),
      ...payouts.value.slice(0, end - payouts.value.length)
    ];
  }
});

function startPayoutScroll() {
  if (payoutScrollInterval) return;
  payoutScrollInterval = setInterval(() => {
    if (!payouts.value || payouts.value.length <= 5) return;
    payoutScrollIndex.value = (payoutScrollIndex.value + 1) % payouts.value.length;
    if (payoutScrollIndex.value > payouts.value.length - 5) {
      payoutScrollIndex.value = 0;
    }
  }, 3000); // Change every 3 seconds
}

function stopPayoutScroll() {
  if (payoutScrollInterval) {
    clearInterval(payoutScrollInterval);
    payoutScrollInterval = null;
  }
}

watch(payouts, (newVal) => {
  if (newVal && newVal.length > 5) {
    startPayoutScroll();
  } else {
    stopPayoutScroll();
    payoutScrollIndex.value = 0;
  }
});

onMounted(() => {
  if (payouts.value && payouts.value.length > 5) {
    startPayoutScroll();
  }
});
onBeforeUnmount(() => {
  stopPayoutScroll();
});

const nextLevelData = computed(() => {
  if (!levels.value || levels.value.length === 0) return null;
  // Start from the next level index
  let idx = level.value;
  // Skip breaks to find the next non-break level
  while (idx < levels.value.length && levels.value[idx]?.isBreak) {
    idx++;
  }
  // If we found a non-break level, return it
  if (idx < levels.value.length) {
    return levels.value[idx];
  }
  // If no more levels, return null
  return null;
});

function getDurationForLevel(levelData) {
  if (levelData) {
    // 1. Use per-level override if it exists and is a valid number
    if (typeof levelData.duration === 'number' && levelData.duration > 0) {
      return levelData.duration * 60;
    }
    // 2. Otherwise, use default break duration for breaks
    if (levelData.isBreak) {
      return breakDurationFromSettings.value * 60;
    }
  }
  // 3. Fallback to default level duration
  return defaultDurationFromSettings.value * 60;
}

function secondsRemaining(now = Date.now()) {
  if (!endTime.value) return timeLeft.value ?? 0;
  return Math.round((endTime.value - now) / 1000);
}

function scheduleCurrentLevelEnd(baseTime = Date.now(), withPause = false) {
  const pauseMs = withPause ? (betweenRoundsPauseSeconds.value * 1000) : 0;
  endTime.value = baseTime + (countdownDuration.value * 1000) + pauseMs;
  // Show the new level's full time frozen; the interval will clamp display to countdownDuration
  timeLeft.value = countdownDuration.value;
}

function advanceToNextLevelUsingPreviousEnd() {
  const nextLevelIndex = level.value;
  if (nextLevelIndex >= levels.value.length) {
    stopTimer();
    return false;
  }

  // Credit the level that just completed before advancing.
  const completedLevelData = currentLevelData.value;
  if (completedLevelData && !completedLevelData.isBreak) {
    elapsedSecondsBeforeCurrentLevel.value += getDurationForLevel(completedLevelData);
    tournamentStore.setElapsedSecondsBeforeCurrentLevel(elapsedSecondsBeforeCurrentLevel.value);
  }

  const nextLevelData = levels.value[nextLevelIndex];
  countdownDuration.value = getDurationForLevel(nextLevelData);

  const previousLevelEndTime = endTime.value ?? Date.now();
  level.value++;
  updateNormalLevelCount();

  scheduleCurrentLevelEnd(previousLevelEndTime, true);
  return true;
}

function handleLevelTransitions(now = Date.now()) {
  if (!timerRunning.value) return false;

  let transitioned = false;

  while (timerRunning.value && endTime.value && now >= endTime.value) {
    playSound(levelEndSound.value);

    const advanced = advanceToNextLevelUsingPreviousEnd();
    if (!advanced) {
      transitioned = true;
      break;
    }
    transitioned = true;
  }

  if (transitioned && timerRunning.value && endTime.value) {
    const rawLeft = Math.max(0, Math.round((endTime.value - Date.now()) / 1000));
    timeLeft.value = Math.min(rawLeft, countdownDuration.value ?? rawLeft);
    saveStateToFirestore();
  }

  return transitioned;
}

function getContrastingTextColor(hexcolor){
  if (!hexcolor) return '#000000';
  // If a leading # is provided, remove it
  if (hexcolor.slice(0, 1) === '#') {
    hexcolor = hexcolor.slice(1);
  }

  // If a three-character hexcode, convert to six
  if (hexcolor.length === 3) {
    hexcolor = hexcolor.split('').map(function (hex) {
      return hex + hex;
    }).join('');
  }

  const r = parseInt(hexcolor.substr(0,2),16);
  const g = parseInt(hexcolor.substr(2,2),16);
  const b = parseInt(hexcolor.substr(4,2),16);
  const yiq = ((r*299)+(g*587)+(b*114))/1000;
  return (yiq >= 128) ? '#000000' : '#FFFFFF';
}

// --- Quick-bust helpers ---
function getActiveEntryId(player) {
  return player.rebuys > 0 ? `${player.id}_rebuy_${player.rebuys}` : player.id;
}

const bustedEntryIdSet = computed(() =>
  new Set(bustedPlayers.value.map(bp => bp.playerId))
);

const activePlayers = computed(() =>
  players.value.filter(p => !bustedEntryIdSet.value.has(getActiveEntryId(p)))
);

const bustingPlayer = computed(() =>
  bustingPlayerId.value
    ? players.value.find(p => String(p.id) === String(bustingPlayerId.value)) ?? null
    : null
);

const bustKnockedOutByOptions = computed(() => {
  const busting = bustingPlayer.value;
  if (!busting) return [];
  const bustingTable = busting.table;
  return activePlayers.value
    .filter(p => String(p.id) !== String(bustingPlayerId.value))
    .filter(p => {
      if (maxTables.value > 0 && bustingTable) return p.table === bustingTable;
      return true;
    })
    .map(p => p.name)
    .filter(name => name && name.trim() !== '');
});

async function confirmTournamentBust() {
  const player = bustingPlayer.value;
  if (!player) return;

  const entryId = getActiveEntryId(player);
  const bustedTable = player.table ?? null;
  const tournamentSecondsAtBust = tournamentSecondsElapsed.value ?? 0;

  bustedPlayers.value.push({
    playerId: entryId,
    knockedOutBy: bustKnockedOutBy.value || '',
    didRebuy: bustDidRebuy.value,
    order: bustedPlayers.value.length + 1,
    timestamp: Date.now(),
    tournamentSecondsAtBust,
  });

  if (bustDidRebuy.value && rebuyAllowed.value) {
    player.rebuys = (player.rebuys || 0) + 1;
    player.enteredTournamentSeconds = tournamentSecondsAtBust;
  }

  const totalRebuysVal = players.value.reduce((t, p) => t + (p.rebuys || 0), 0);
  const totalAddOnsVal = players.value.reduce((t, p) => t + (p.addOn ? 1 : 0), 0);

  try {
    await setDoc(doc(db, TOURNAMENT_PLAYERS_DB, newTournamentName.value), {
      players: players.value,
      bustedPlayers: bustedPlayers.value,
      totalRebuys: totalRebuysVal,
      totalAddOns: totalAddOnsVal,
    });
    tournamentStore.players = players.value;
    tournamentStore.bustedPlayers = bustedPlayers.value;
  } catch (e) {
    console.error('Error saving bust:', e);
  }

  // Check table balance if player was seated and did not rebuy
  if (!bustDidRebuy.value && maxTables.value > 0 && bustedTable) {
    const tableMap = {};
    for (let i = 1; i <= maxTables.value; i++) tableMap[i] = [];
    players.value.forEach(p => {
      if (p.table && !bustedEntryIdSet.value.has(getActiveEntryId(p))) {
        tableMap[p.table].push(p);
      }
    });
    if (isTableUnbalancedAfterBust(tableMap, bustedTable)) {
      bustRebalanceDialog.value = true;
    }
  }

  bustDialog.value = false;
  bustingPlayerId.value = null;
  bustKnockedOutBy.value = '';
  bustDidRebuy.value = false;
}

function cancelTournamentBust() {
  bustDialog.value = false;
  bustingPlayerId.value = null;
  bustKnockedOutBy.value = '';
  bustDidRebuy.value = false;
}

function setupPlayersListener() {
  if (playersUnsub) {
    playersUnsub();
  }

  if (!newTournamentName.value) {
    //console.log('No tournament name available for players listener');
    players.value = [];
    bustedPlayers.value = [];
    return;
  }

  //console.log('Setting up players listener for tournament:', newTournamentName.value);
  
  const playersDocRef = doc(db, TOURNAMENT_PLAYERS_DB, newTournamentName.value);
  playersUnsub = onSnapshot(playersDocRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      //console.log('Players data received:', data);
      players.value = Array.isArray(data.players) ? data.players : [];
      bustedPlayers.value = Array.isArray(data.bustedPlayers) ? data.bustedPlayers : [];

      // If not tracking by name, load manual counts
      if (!trackPlayersByName.value) {
        manualTotalPlayers.value = data.totalPlayers || 0;
        manualTotalRebuys.value = data.totalRebuys || 0;
        manualTotalAddOns.value = data.totalAddOns || 0;
        bustedPlayerCount.value = data.bustedPlayerCount || 0;
      }
      //console.log('Updated players array:', players.value);
      //console.log('Updated busted players array:', bustedPlayers.value);
    } else {
      //console.log('No players document found');
      players.value = [];
      bustedPlayers.value = [];
    }
  }, (error) => {
    console.error('Error listening to players data:', error);
  });
}

async function loadTournamentSettings() {
  if (!newTournamentName.value) return;
  const settingsDoc = doc(db, TOURNAMENT_SETTINGS_DB, newTournamentName.value);
  try {
    const docSnap = await getDoc(settingsDoc);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (typeof data.buyinAmount === 'number') {
        buyinAmount.value = data.buyinAmount;
      } else {
        console.warn('buyinAmount missing or invalid in settings');
      }
      if (typeof data.rebuyAmount === 'number') {
        rebuyAmount.value = data.rebuyAmount;
      }
      if (typeof data.addonAmount === 'number') {
        addonAmount.value = data.addonAmount;
      }
      if (typeof data.rebuyAllowed === 'boolean') {
        rebuyAllowed.value = data.rebuyAllowed;
      }
      if (typeof data.rebuyEndLevel === 'number' || data.rebuyEndLevel === null) {
        rebuyEndLevel.value = data.rebuyEndLevel ?? null;
      }
      if (typeof data.addonAllowed === 'boolean') {
        addonAllowed.value = data.addonAllowed;
      }
      if (typeof data.buyinChips === 'number') {
        buyinChips.value = data.buyinChips;
      }
      if (typeof data.addonChips === 'number') {
        addonChips.value = data.addonChips;
      }
      if (typeof data.maxTables === 'number') {
        maxTables.value = data.maxTables;
      }
      if (typeof data.maxPlayersPerTable === 'number') {
        maxPlayersPerTable.value = data.maxPlayersPerTable;
      }
      console.log('[loadTournamentSettings] trackPlayersByName from DB:', data.trackPlayersByName);
      if (typeof data.trackPlayersByName === 'boolean') {
        trackPlayersByName.value = data.trackPlayersByName;
      }
      if (typeof data.startCountdownSeconds === 'number') {
        startCountdownSeconds.value = data.startCountdownSeconds;
      }
      if (typeof data.betweenRoundsPauseSeconds === 'number') {
        betweenRoundsPauseSeconds.value = data.betweenRoundsPauseSeconds;
      }
      if (typeof data.buyinRake === 'number') buyinRake.value = data.buyinRake;
      if (typeof data.rebuyRake === 'number') rebuyRake.value = data.rebuyRake;
      if (typeof data.addonRake === 'number') addonRake.value = data.addonRake;
      if (Array.isArray(data.chipDenominations)) {
        chipDenominations.value = data.chipDenominations;
      }
      if (data.shareId) {
        shareId.value = data.shareId;
      }
      if (data.tournamentAlias) {
        shareId.value = data.tournamentAlias;
      }
      if (data.codeName) {
        shareId.value = data.codeName;
      }
      if (typeof data.announcement?.text === 'string') {
        postedAnnouncementText.value = data.announcement.text;
      }
      displayConfig.value = mergeDisplayConfig(data.displayConfig);
      // Load payout tables from settings
      if (Array.isArray(data.payoutTables) && data.payoutTables.length > 0) {
        payoutTables.value = data.payoutTables;
      } else {
        // Default payout tables if none exist
        payoutTables.value = [
          {
            minEntries: 1,
            maxEntries: 8,
            numberOfPayouts: 3,
            prizesTemplate: [50, 30, 20],
            rounding: 'roundToNearestDollar'
          },
          {
            minEntries: 9,
            maxEntries: 16,
            numberOfPayouts: 4,
            prizesTemplate: [40, 25, 20, 15],
            rounding: 'roundToNearestDollar'
          },
          {
            minEntries: 17,
            maxEntries: 999,
            numberOfPayouts: 5,
            prizesTemplate: [35, 22, 18, 15, 10],
            rounding: 'roundToNearestDollar'
          }
        ];
      }
    }
  } catch (e) {
    console.error('Error loading tournament settings:', e);
  }
}

async function saveStateToFirestore() {
  if (!newTournamentName.value) {
    setFeedbackMessage('No tournament name set. Cannot save timer.', true);
    return;
  }
  const timerRef = doc(db, TOURNAMENT_TIMERS_DB, newTournamentName.value);
  const timerData = {
    timeLeft: timeLeft.value,
    level: level.value,
    normalLevelCount: normalLevelCount.value,
    timerRunning: timerRunning.value,
    endTime: timerRunning.value ? endTime.value : null,
    savedAt: serverTimestamp(),
    countdownDuration: countdownDuration.value,
    tournamentSecondsElapsed: tournamentSecondsElapsed.value,
    elapsedSecondsBeforeCurrentLevel: elapsedSecondsBeforeCurrentLevel.value
  };
  try {
    loading.value = true;
    const existing = await getDoc(timerRef);
    if (existing.exists() && existing.data().tournamentStartedAt != null) {
      timerData.tournamentStartedAt = existing.data().tournamentStartedAt;
    }
    await setDoc(timerRef, timerData);
    setFeedbackMessage('Timer state saved.');
    loading.value = false;
  } catch (e) {
    console.error('Error saving timer state to Firestore:', e);
    setFeedbackMessage('Error saving timer state.', true);
    loading.value = false;
  }
}

// When loading from Firestore, set endTime and timeLeft appropriately
async function loadStateFromFirestore() {
  if (!newTournamentName.value) {
    setFeedbackMessage('No tournament name set. Cannot load timer.', true);
    return;
  }
  const timerDoc = doc(db, TOURNAMENT_TIMERS_DB, newTournamentName.value);
  try {
    loading.value = true;
    const docSnap = await getDoc(timerDoc);
    if (docSnap.exists()) {
      const state = docSnap.data();
      if (typeof state.countdownDuration === 'number') countdownDuration.value = state.countdownDuration;
      if (typeof state.level === 'number') level.value = state.level;
      if (typeof state.normalLevelCount === 'number') normalLevelCount.value = state.normalLevelCount;
      if (typeof state.timerRunning === 'boolean') timerRunning.value = state.timerRunning;

      // Restore elapsedSecondsBeforeCurrentLevel — only changes at level transitions so it's
      // always accurate in Firestore (saved on every level transition via saveStateToFirestore).
      if (typeof state.elapsedSecondsBeforeCurrentLevel === 'number') {
        elapsedSecondsBeforeCurrentLevel.value = Math.max(
          state.elapsedSecondsBeforeCurrentLevel,
          elapsedSecondsBeforeCurrentLevel.value
        );
      } else if (typeof state.tournamentSecondsElapsed === 'number') {
        // Backward compat: pre-migration Firestore docs have no elapsedSecondsBeforeCurrentLevel.
        // Estimate it by subtracting the current level's partial from the stored total.
        const storedPartial = Math.max(0, (state.countdownDuration ?? countdownDuration.value) - (state.timeLeft ?? timeLeft.value));
        elapsedSecondsBeforeCurrentLevel.value = Math.max(0, state.tournamentSecondsElapsed - storedPartial);
      }

      if (typeof state.endTime === 'number' && state.timerRunning) {
        let newEndTime = state.endTime;
        let newLevel = state.level;
        let timeSinceEnd = (Date.now() - newEndTime) / 1000;

        // Fast-forward through levels that completed while the tab was backgrounded / component unmounted.
        // Each completed non-break level is credited to elapsedSecondsBeforeCurrentLevel.
        if (timeSinceEnd > 0) {
          while (timeSinceEnd > 0 && newLevel < levels.value.length) {
            const nextLevelData = levels.value[newLevel]; // 0-indexed: newLevel === current 1-based level
            const nextLevelDuration = getDurationForLevel(nextLevelData);
            if (!nextLevelData?.isBreak) {
              elapsedSecondsBeforeCurrentLevel.value += nextLevelDuration;
            }
            timeSinceEnd -= nextLevelDuration;
            newEndTime += nextLevelDuration * 1000;
            newLevel++;
          }
        }
        endTime.value = newEndTime;
        level.value = newLevel;
        // Ensure countdownDuration reflects the current level after fast-forward
        if (levels.value[newLevel - 1]) {
          countdownDuration.value = getDurationForLevel(levels.value[newLevel - 1]);
        }
        const rawLeft = Math.max(0, Math.round((newEndTime - Date.now()) / 1000));
        timeLeft.value = Math.min(rawLeft, countdownDuration.value ?? rawLeft);
      } else if (typeof state.timeLeft === 'number') {
        endTime.value = null;
        timeLeft.value = state.timeLeft;
      }

      // Recompute tournamentSecondsElapsed from wall clock — mirrors timeLeft logic, never drifts.
      const recomputedElapsed = elapsedSecondsBeforeCurrentLevel.value +
        (currentLevelData.value?.isBreak ? 0 : Math.max(0, countdownDuration.value - timeLeft.value));
      tournamentSecondsElapsed.value = recomputedElapsed;
      tournamentStore.setTournamentSecondsElapsed(recomputedElapsed);
      tournamentStore.setElapsedSecondsBeforeCurrentLevel(elapsedSecondsBeforeCurrentLevel.value);
      if (!trackPlayersByName.value) {
        const playersDoc = doc(db, TOURNAMENT_PLAYERS_DB, newTournamentName.value);
        const playersSnap = await getDoc(playersDoc);
        if (playersSnap.exists()) {
          bustedPlayerCount.value = playersSnap.data().bustedPlayerCount || 0;
        }
      }
      updateNormalLevelCount();
      setFeedbackMessage('Timer state loaded.');
    } else {
      setFeedbackMessage('No timer state found in Firestore.');
    }
    loading.value = false;
  } catch (e) {
    console.error('Error loading timer state from Firestore:', e);
    setFeedbackMessage('Error loading timer state.', true);
    loading.value = false;
  }
}

async function loadDefaultDurationFromSettings() {
  if (!newTournamentName.value) {
    setFeedbackMessage('No tournament name set. Cannot load settings.', true);
    return;
  }
  if (!db) {
    setFeedbackMessage('Firestore not initialized.', true);
    return;
  }
  const settingsDoc = doc(db, TOURNAMENT_SETTINGS_DB, newTournamentName.value);
  try {
    loading.value = true;
    //console.log('Loading default duration from Firestore for tournament:', newTournamentName.value);
    const docSnap = await getDoc(settingsDoc);
    if (docSnap.exists()) {
      const data = docSnap.data();
      //console.log('Loaded default duration:', data);
      if (typeof data.defaultDuration === 'number') {
        defaultDurationFromSettings.value = data.defaultDuration;
        if (countdownDuration.value === null) {
          countdownDuration.value = data.defaultDuration * 60;
          timeLeft.value = countdownDuration.value;
        }
      }
      if (typeof data.breakDuration === 'number') {
        breakDurationFromSettings.value = data.breakDuration;
      }
      setFeedbackMessage('Default duration loaded.');
    } else {
      setFeedbackMessage('No default duration found in Firestore.');
      //console.log('No default duration found for tournament:', newTournamentName.value);
    }
    loading.value = false;
  } catch (e) {
    console.error('Error loading default duration from Firestore:', e);
    setFeedbackMessage('Error loading default duration.', true);
    loading.value = false;
  }
}

async function loadLevelsFromSettings() {
  if (!newTournamentName.value) {
    setFeedbackMessage('No tournament name set. Cannot load levels.', true);
    return;
  }
  const settingsDoc = doc(db, TOURNAMENT_SETTINGS_DB, newTournamentName.value);
  try {
    const docSnap = await getDoc(settingsDoc);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (Array.isArray(data.levels)) {
        levels.value = data.levels;
      }
      if (Array.isArray(data.payoutTables)) {
        payoutTables.value = data.payoutTables;
      }
    }
  } catch (e) {
    console.error('Error loading levels from settings:', e);
    // Optionally handle error
  }
}

/*function getCurrentTimeLeft() {
  if (timerRunning.value && endTime.value) {
    return Math.max(0, Math.round((endTime.value - Date.now()) / 1000));
  }
  return timeLeft.value;
}*/

function startTimer() {
  if (timerInterval) return;
  if (playersLeft.value < 2) {
    setFeedbackMessage('Need at least 2 players to run the timer.', true);
    return;
  }
  timerRunning.value = true;

  if (isPreCountdown.value) {
    // Resuming a paused pre-countdown — pick up from where it left off
    preCountdownEndTime.value = Date.now() + (preCountdownTimeLeft.value * 1000);
  } else {
    // Pre-tournament countdown: only on the very first start (level 1, no elapsed time, full time remaining)
    const isFirstStart = level.value === 1
      && elapsedSecondsBeforeCurrentLevel.value === 0
      && timeLeft.value === countdownDuration.value
      && startCountdownSeconds.value > 0;

    if (isFirstStart) {
      isPreCountdown.value = true;
      preCountdownTimeLeft.value = startCountdownSeconds.value;
      preCountdownEndTime.value = Date.now() + (startCountdownSeconds.value * 1000);
      // Play tournament-start sound if the file exists (silent fail if missing)
      if (tournamentStartSound.value) {
        tournamentStartSound.value.play().catch(() => {});
      }
    } else {
      endTime.value = Date.now() + (timeLeft.value * 1000);
      console.log('Timer started, endTime set to:', new Date(endTime.value).toLocaleString(), 'with timeLeft (s):', timeLeft.value);
    }
  }

  let prevTickTimeLeft = timeLeft.value;
  timerInterval = setInterval(() => {
    if (!timerRunning.value) return;
    const now = Date.now();

    // Pre-countdown phase: count down before Level 1 begins
    if (isPreCountdown.value) {
      const left = Math.max(0, Math.round((preCountdownEndTime.value - now) / 1000));
      preCountdownTimeLeft.value = left;
      if (left <= 0) {
        isPreCountdown.value = false;
        preCountdownEndTime.value = null;
        endTime.value = Date.now() + (timeLeft.value * 1000);
        console.log('Pre-countdown ended, timer starting at:', new Date(endTime.value).toLocaleString());
        saveStateToFirestore();
      }
      return;
    }

    // Normal level countdown — clamp display to countdownDuration to hide the between-rounds pause window
    let left = Math.max(0, secondsRemaining(now));
    timeLeft.value = Math.min(left, countdownDuration.value ?? left);

    // Derive tournament time from wall clock — same logic as timeLeft, so it never drifts.
    // elapsedSecondsBeforeCurrentLevel accumulates at level transitions; current-level
    // partial is countdownDuration - timeLeft (wall-clock accurate).
    const elapsed = elapsedSecondsBeforeCurrentLevel.value +
      (currentLevelData.value?.isBreak ? 0 : Math.max(0, countdownDuration.value - timeLeft.value));
    tournamentSecondsElapsed.value = elapsed;
    tournamentStore.setTournamentSecondsElapsed(elapsed);

    // Play one-minute warning when the display crosses 60. Checking for a crossing rather than
    // exactly 60 matters on iPads, where throttled timers can skip the 60 tick entirely.
    if (prevTickTimeLeft > 60 && timeLeft.value <= 60 && timeLeft.value > 0) {
      playSound(oneMinuteWarningSound.value);
    }
    prevTickTimeLeft = timeLeft.value;

    // Level transition triggers on the raw wall-clock value, which accounts for the between-rounds pause
    if (left <= 0) {
      const transitioned = handleLevelTransitions(now);
      if (!transitioned && timerRunning.value) {
        timeLeft.value = Math.max(0, secondsRemaining());
      }

  // Persist in the background: the on-screen countdown must not wait on Firestore round trips
  // (slow connections stalled the display for ~30s). endTime is already set, so the display
  // is correct from the first tick regardless of when these writes land.
  persistTimerStart();
}

async function persistTimerStart() {
  await saveStateToFirestore();
  // Set tournament start time once (for duration-in-tournament when saving results)
  try {
    const timersDocRef = doc(db, TOURNAMENT_TIMERS_DB, newTournamentName.value);
    const snap = await getDoc(timersDocRef);
    if (snap.exists() && snap.data().tournamentStartedAt == null) {
      await updateDoc(timersDocRef, { tournamentStartedAt: Date.now() });
    }
  } catch (e) {
    console.warn('Could not set tournamentStartedAt', e);
  }
    }
  }, 1000);
}

function stopTimer({ keepRunningFlag = false } = {}) {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  if (!keepRunningFlag) {
    timerRunning.value = false;
    if (isPreCountdown.value) {
      // Pause mid-countdown: preserve remaining time so Start resumes from this point
      preCountdownEndTime.value = null;
    } else if (endTime.value) {
      const raw = Math.max(0, Math.round((endTime.value - Date.now()) / 1000));
      timeLeft.value = Math.min(raw, countdownDuration.value ?? raw);
    }
    endTime.value = null;
  }
  console.log('Timer stopped, timeLeft (s):', timeLeft.value);
  saveStateToFirestore(); // Save immediately with timer stopped
}

function resetLevel() {
  countdownDuration.value = getDurationForLevel(currentLevelData.value);
  scheduleCurrentLevelEnd(Date.now());
  console.log('Level reset to duration (s):', countdownDuration.value);
  saveStateToFirestore();
}

// Dialog state for reset all confirmation
const resetAllDialog = ref(false);

function confirmResetAll() {
  resetAllDialog.value = true;
}

function cancelResetAll() {
  resetAllDialog.value = false;
}

async function doResetAll() {
  // If the timer is running, stop it first.
  if (timerRunning.value) {
    stopTimer();
  }
  // Clear any in-progress pre-countdown so the next Start begins fresh
  isPreCountdown.value = false;
  preCountdownEndTime.value = null;
  preCountdownTimeLeft.value = 0;

  // Load the current structure from settings first
  await loadLevelsFromSettings();
  await loadTournamentSettings();
  
  // Remove busts, rebuys, and add-ons from players (local)
  players.value = players.value.map(player => ({
    ...player,
    rebuys: 0,
    addOn: false,
  }));
  bustedPlayers.value = [];
  
  // Also update Firestore
  await setDoc(doc(db, TOURNAMENT_PLAYERS_DB, newTournamentName.value), {
    players: players.value,
    bustedPlayers: [],
  });
  
  // Recalculate payouts using the loaded payout tables
  const { recalculateAndSavePayouts } = usePayouts();
  await recalculateAndSavePayouts({
    userId: user.value.uid,
    tournamentId: newTournamentName.value,
    players: players.value,
    buyinAmount: buyinAmount.value,
    rebuyAmount: rebuyAmount.value,
    addonAmount: addonAmount.value,
    rebuyAllowed: rebuyAllowed.value,
    addonAllowed: addonAllowed.value,
    payoutTables: payoutTables.value,
    buyinRake: buyinRake.value,
    rebuyRake: rebuyRake.value,
    addonRake: addonRake.value,
  });
  
  // Reset timer and levels using the loaded structure
  countdownDuration.value = getDurationForLevel(levels.value[0]);
  scheduleCurrentLevelEnd(Date.now());
  level.value = 1;
  normalLevelCount.value = 1;
  tournamentSecondsElapsed.value = 0;
  tournamentStore.setTournamentSecondsElapsed(0);
  elapsedSecondsBeforeCurrentLevel.value = 0;
  tournamentStore.setElapsedSecondsBeforeCurrentLevel(0);
  saveStateToFirestore();
  resetAllDialog.value = false;
}

// New functions for the select tournament dialog
async function loadSelectedTournament(tournamentId) {
  if (!tournamentId) return;
  
  // Set the tournament name in the store and local state
  tournamentStore.setTournamentName(tournamentId);
  newTournamentName.value = tournamentId;
  
  // Set tournament as active
  tournamentActive.value = true;
  
  // Close the dialog
  showSelectTournamentDialog.value = false;

  // Manually trigger data loading
  await loadAllTournamentData();
}

function handleCreateNewFromSelect() {
  // Close the selection dialog
  showSelectTournamentDialog.value = false;
  // Open the create dialog
  openCreateDialog();
}

function incrementLevel() {
  level.value++;
  console.log('Level incremented to:', level.value);
  updateNormalLevelCount();
  saveStateToFirestore();
}

function decrementLevel() {
  if (level.value > 1) {
    level.value--;
    console.log('Level decremented to:', level.value);
    updateNormalLevelCount();
    saveStateToFirestore();
  }
}

// Function to calculate and update normal level count
function updateNormalLevelCount() {
  if (!levels.value || levels.value.length === 0) {
    normalLevelCount.value = 1;
    return;
  }
  
  let count = 0;
  // Count only completed normal levels (up to current level - 1)
  for (let i = 0; i < Math.min(level.value - 1, levels.value.length); i++) {
    if (!levels.value[i].isBreak) {
      count++;
    }
  }
  // Add 1 for current level if it's a normal level
  if (level.value <= levels.value.length && !levels.value[level.value - 1]?.isBreak) {
    count++;
  }
  normalLevelCount.value = count;
}

// Load tournament structures for the create dialog
async function loadStructureOptions() {
  if (!userStore.user) {
    //console.log('No user ID available for loading structures');
    return;
  }
  
  try {
    const structuresCol = collection(db, TOURNAMENT_STRUCTURES_DB);
    
    // Query 1: Structures owned by the current user, matched on the stored user snapshot's user_id
    // Exclude template structures (templates are only shown in SettingsView)
    const userStructuresQuery = query(structuresCol, where('user.user_id', '==', userStore.userid));
    
    // Query 2: Global structures (also exclude templates)
    const globalStructuresQuery = query(structuresCol, where('isGlobal', '==', true));

    const [userSnapshot, globalSnapshot] = await Promise.all([
      getDocs(userStructuresQuery),
      getDocs(globalStructuresQuery)
    ]);

    // Use Map to deduplicate structures (in case a structure is both user-owned and global)
    // Filter out template structures
    const allStructures = new Map();
    const processSnapshot = (snapshot) => {
      snapshot.forEach(doc => {
        const data = doc.data();
        // Exclude template structures from tournament creation
        if (!data.isTemplate) {
          allStructures.set(doc.id, { id: doc.id, name: data.name, ...data });
        }
      });
    };

    processSnapshot(userSnapshot);
    processSnapshot(globalSnapshot);

    structureOptions.value = Array.from(allStructures.values());
  } catch (e) {
    console.error('Error loading tournament structures:', e);
  }
}

// Open create dialog and ensure structures are loaded
async function openCreateDialog() {
  // Ensure structures are loaded before opening dialog
  createDialogFeedback.value = ''; // Clear old feedback
  selectedLeagueId.value = null;
  await loadStructureOptions();
  if (user.value?.uid && userStore.username) {
    availableLeagues.value = await getLeaguesForRunner(user.value.uid, userStore.username);
  }
  showCreateDialog.value = true;
}

// Handle structure selection in the create dialog
function onStructureSelect(structureId) {
  if (!structureId) {
    selectedStructure.value = null;
    return;
  }
  
  const structure = structureOptions.value.find(s => s.id === structureId);
  if (structure) {
    selectedStructure.value = structure;
  }
}

// Create a new tournament
async function createTournament() {
  if (!newTournamentName.value || !selectedStructureId.value || !user.value?.uid) {
    return;
  }
  const tournamentName = (newTournamentName.value || '').trim();
  if (!tournamentName) return;

  // When user provides a custom code, check it's not already in use (case-insensitive)
  const aliasTrimmed = (newTournamentAlias.value || '').trim();
  if (aliasTrimmed) {
    const aliasNormalized = aliasTrimmed.toLowerCase();
    try {
      const settingsCol = collection(db, TOURNAMENT_SETTINGS_DB);
      const qLower = query(settingsCol, where('codeNameLower', '==', aliasNormalized));
      let querySnapshot = await getDocs(qLower);
      if (querySnapshot.empty) {
        const qExact = query(settingsCol, where('codeName', '==', aliasTrimmed));
        querySnapshot = await getDocs(qExact);
      }
      const activeTournamentExists = !querySnapshot.empty;
      if (activeTournamentExists) {
        createDialogFeedback.value = `Tournament code "${newTournamentAlias.value}" is already in use. Please choose another.`;
        return;
      }
    } catch (e) {
      console.error('Error checking for existing tournament code:', e);
      createDialogFeedback.value = 'Error checking for existing tournament code. Please try again.';
      return;
    }
  }

  // Set the tournament name in the store so watchers and other components can access it
  tournamentStore.setTournamentName(tournamentName);
  newTournamentName.value = tournamentName;

  try {
    const structure = structureOptions.value.find(s => s.id === selectedStructureId.value);
    if (!structure) {
      console.error('Selected structure not found');
      return;
    }

    // Initialize tournament with structure settings
    levels.value = structure.levels || [];
    buyinAmount.value = structure.buyinAmount || 0;
    rebuyAmount.value = structure.rebuyAmount || 0;
    addonAmount.value = structure.addonAmount || 0;
    rebuyAllowed.value = structure.rebuyAllowed || false;
    rebuyEndLevel.value = structure.rebuyEndLevel ?? null;
    addonAllowed.value = structure.addonAllowed || false;
    buyinChips.value = structure.buyinChips || 1000;
    addonChips.value = structure.addonChips || 1000;
    trackPlayersByName.value = structure.trackPlayersByName ?? false;
    chipDenominations.value = structure.chipDenominations || [];
    defaultDurationFromSettings.value = structure.defaultDuration || 12;
    breakDurationFromSettings.value = structure.breakDuration || 5;
    startCountdownSeconds.value = structure.startCountdownSeconds ?? 0;
    betweenRoundsPauseSeconds.value = structure.betweenRoundsPauseSeconds ?? 0;
    buyinRake.value = structure.buyinRake ?? 0;
    rebuyRake.value = structure.rebuyRake ?? 0;
    addonRake.value = structure.addonRake ?? 0;
    payoutTables.value = structure.payoutTables || [];
    displayConfig.value = mergeDisplayConfig(structure.displayConfig);

    // Set initial countdown duration and timeLeft
    countdownDuration.value = getDurationForLevel(levels.value[0]);
    timeLeft.value = countdownDuration.value;

    // Watch code: only set when user enters a code; no code = no way to watch
    const codeName = aliasTrimmed;
    const codeNameLower = codeName ? codeName.toLowerCase() : '';

    // Save tournament settings with the tournamentName as the document ID
    await setDoc(doc(db, TOURNAMENT_SETTINGS_DB, tournamentName), {
      ownerId: user.value.uid,
      tournamentName, // Save the tournamentName as a field
      codeName: codeName || '', // Empty = no watch link
      codeNameLower,
      defaultDuration: defaultDurationFromSettings.value,
      breakDuration: breakDurationFromSettings.value,
      levels: levels.value,
      structureName: structure.name,
      buyinAmount: buyinAmount.value,
      rebuyAmount: rebuyAmount.value,
      addonAmount: addonAmount.value,
      rebuyAllowed: rebuyAllowed.value,
      rebuyEndLevel: rebuyEndLevel.value,
      addonAllowed: addonAllowed.value,
      buyinChips: buyinChips.value,
      addonChips: addonChips.value,
      maxTables: structure.maxTables ?? 0,
      maxPlayersPerTable: structure.maxPlayersPerTable ?? 10,
      trackPlayersByName: trackPlayersByName.value,
      chipDenominations: chipDenominations.value,
      payoutTables: payoutTables.value,
      isGlobal: false, // New tournaments are not global by default
      leagueId: selectedLeagueId.value || null,
      startCountdownSeconds: startCountdownSeconds.value,
      betweenRoundsPauseSeconds: betweenRoundsPauseSeconds.value,
      buyinRake: buyinRake.value,
      rebuyRake: rebuyRake.value,
      addonRake: addonRake.value,
      displayConfig: displayConfig.value,
    });

    const initialPlayerData = trackPlayersByName.value
      ? { players: [], bustedPlayers: [] }
      : {
          totalPlayers: 0,
          totalRebuys: 0,
          totalAddOns: 0,
          bustedPlayerCount: 0,
        };

    // Save players, payouts, and timers using the tournamentName as the document ID
    await Promise.all([
      setDoc(doc(db, TOURNAMENT_PLAYERS_DB, tournamentName), initialPlayerData),
      setDoc(doc(db, TOURNAMENT_PAYOUTS_DB, tournamentName), {
        numberOfPayouts: 0,
        payouts: []
      }),
      console.log('Initializing tournament timer for:', tournamentName),
      setDoc(doc(db, TOURNAMENT_TIMERS_DB, tournamentName), {
        timeLeft: timeLeft.value,
        level: 1,
        normalLevelCount: 1,
        timerRunning: false,
        endTime: null,
        countdownDuration: countdownDuration.value,
        tournamentSecondsElapsed: 0,
        elapsedSecondsBeforeCurrentLevel: 0
      })
    ]);

    // Clear stale player data from the previous tournament so PlayersView
    // doesn't pick up old players when it initialises from the store.
    tournamentStore.players = [];
    tournamentStore.bustedPlayers = [];
    players.value = [];
    bustedPlayers.value = [];

    // Set the share ID for the watch link (only when a code was entered)
    shareId.value = codeName || '';

    // Close dialog and activate tournament
    showCreateDialog.value = false;
    //newTournamentName.value = '';
    newTournamentAlias.value = '';
    selectedStructureId.value = null;
    selectedStructure.value = null;
    selectedLeagueId.value = null;
    createDialogFeedback.value = ''; // Clear feedback on success
    tournamentActive.value = true;

    // Update normal level count
    updateNormalLevelCount();

    setFeedbackMessage(`Tournament "${structure.name}" created successfully!`);

    // Manually trigger payout calculation for the new tournament
    await initializePayoutsFromTemplate();

    // Load all data for the new tournament to ensure UI is in sync
    await loadAllTournamentData();
  } catch (e) {
    console.error('Error creating tournament:', e);
    createDialogFeedback.value = 'Error creating tournament. Please try again.';
  }
}

// When user confirms "Yes, End Tournament": optionally show save-results dialog first
function onConfirmEndTournament() {
  showEndTournamentDialog.value = false;
  if (trackPlayersByName.value) {
    showSaveResultsDialog.value = true;
  } else {
    endTournament();
  }
}

function onDeclineSaveResults() {
  showSaveResultsDialog.value = false;
  endTournament();
}

async function onConfirmSaveResults() {
  showSaveResultsDialog.value = false;
  try {
    await saveTournamentResults();
    setFeedbackMessage('Tournament results saved.');
  } catch (e) {
    console.error('Error saving tournament results:', e);
    setFeedbackMessage('Failed to save results. Ending tournament anyway.', true);
  }
  endTournament();
}

function openPayoutEditor() {
  showSaveResultsDialog.value = false;
  const playersByPlace = new Map(orderedPlayersForEditor.value.map(p => [p.place, p.name]));
  editablePayouts.value = (payouts.value || []).map((p, i) => ({
    position: p?.position ?? i + 1,
    amount: p?.amount ?? 0,
    playerName: playersByPlace.get(p?.position ?? i + 1) || ''
  }));
  showPayoutEditorDialog.value = true;
}

function addPayoutPlace() {
  const nextPosition = editablePayouts.value.length + 1;
  const playersByPlace = new Map(orderedPlayersForEditor.value.map(p => [p.place, p.name]));
  editablePayouts.value.push({
    position: nextPosition,
    amount: 0,
    playerName: playersByPlace.get(nextPosition) || ''
  });
}

function removePayoutPlace(index) {
  editablePayouts.value.splice(index, 1);
  editablePayouts.value.forEach((p, i) => { p.position = i + 1; });
}

function onPayoutEditorSave() {
  if (editablePayoutsRemaining.value !== 0) {
    showPayoutWarningDialog.value = true;
  } else {
    confirmPayoutEditorSave();
  }
}

async function confirmPayoutEditorSave() {
  showPayoutWarningDialog.value = false;
  showPayoutEditorDialog.value = false;
  try {
    await saveTournamentResults(editablePayouts.value);
    setFeedbackMessage('Tournament results saved.');
  } catch (e) {
    console.error('Error saving tournament results:', e);
    setFeedbackMessage('Failed to save results. Ending tournament anyway.', true);
  }
  endTournament();
}

// Build and save tournament results to Firestore (only for tournaments that track players by name)
async function saveTournamentResults(customPayouts = null) {
  if (!newTournamentName.value || !trackPlayersByName.value) return;
  const tournamentNameToEnd = newTournamentName.value;
  const endedAt = Date.now();

  const settingsDocRef = doc(db, TOURNAMENT_SETTINGS_DB, tournamentNameToEnd);
  const settingsSnap = await getDoc(settingsDocRef);
  const ownerId = settingsSnap.exists() ? (settingsSnap.data().ownerId || user.value?.uid) : user.value?.uid;

  const currentUserUid = userStore.userid;
  console.log('[saveTournamentResults] currentUserUid:', currentUserUid);
  console.log('[saveTournamentResults] players.value:', JSON.stringify(players.value.map(p => ({ id: p.id, name: p.name, linkedUserAlias: p.linkedUserAlias, linkedUserId: p.linkedUserId }))));
  if (currentUserUid) {
    for (const p of players.value) {
      if (p.linkedUserAlias) { console.log(`[saveTournamentResults] Player ${p.name} already has linkedUserAlias: ${p.linkedUserAlias}`); continue; }
      const name = (p.name || '').trim();
      if (!name) continue;
      const result = await lookupUserByAlias(name);
      console.log(`[saveTournamentResults] lookupUserByAlias('${name}'):`, result ? { alias: result.alias, allowedTournamentEnterers: result.allowedTournamentEnterers } : null);
      if (result && result.allowedTournamentEnterers.includes(currentUserUid)) {
        p.linkedUserAlias = result.alias;
        p.linkedUserId = result.user_id;
        console.log(`[saveTournamentResults] Linked ${name} → ${result.alias}`);
      }
    }
  }

  const timersDocRef = doc(db, TOURNAMENT_TIMERS_DB, tournamentNameToEnd);
  const timersSnap = await getDoc(timersDocRef);
  // Use tournament clock time only (excludes breaks and paused time).
  // Prefer the in-memory value — it is updated every second by the timer.
  // The Firestore value is only written on level transitions, so it can lag by up to one
  // full level duration if the user saves results mid-level.
  const firestoreSeconds = timersSnap.exists() && typeof timersSnap.data().tournamentSecondsElapsed === 'number'
    ? timersSnap.data().tournamentSecondsElapsed
    : 0;
  const currentTournamentSeconds = tournamentSecondsElapsed.value || firestoreSeconds;

  const N = players.value.length;
  const payoutSource = customPayouts ?? payouts.value;
  const payoutAmounts = (payoutSource || []).map(p => p?.amount ?? 0);
  const isCurrentlyBusted = (player) => {
    const rebuys = player.rebuys || 0;
    const currentEntryId = rebuys > 0 ? `${player.id}_rebuy_${rebuys}` : String(player.id);
    return bustedPlayers.value.some(b => String(b.playerId) === currentEntryId);
  };
  const baseId = (playerId) => String(playerId).includes('_rebuy_')
    ? String(playerId).split('_rebuy_')[0]
    : String(playerId);

  const activePlayers = players.value.filter(p => !isCurrentlyBusted(p));
  const bustedByPlayer = new Map();
  bustedPlayers.value.forEach(b => {
    const key = baseId(b.playerId);
    const current = bustedByPlayer.get(key);
    if (current == null || b.order > current.order) {
      bustedByPlayer.set(key, {
        order: b.order,
        timestamp: b.timestamp,
        tournamentSecondsAtBust: b.tournamentSecondsAtBust,
        knockedOutBy: b.knockedOutBy ?? ''
      });
    }
  });
  const bustedSorted = [...bustedByPlayer.entries()]
    .filter(([id]) => {
      const player = players.value.find(pl => String(pl.id) === id);
      return player ? isCurrentlyBusted(player) : false;
    })
    .map(([playerId, data]) => ({
      playerId,
      order: data.order,
      exitTimestamp: data.timestamp,
      tournamentSecondsAtBust: data.tournamentSecondsAtBust,
      knockedOutBy: data.knockedOutBy ?? ''
    }))
    .sort((a, b) => a.order - b.order);

  const playerResults = [];
  for (let i = 0; i < activePlayers.length; i++) {
    const p = activePlayers[i];
    const totalPaid = buyinAmount.value + (p.rebuys || 0) * rebuyAmount.value + (p.addOn ? addonAmount.value : 0);
    const durationSeconds = currentTournamentSeconds;
    playerResults.push({
      name: p.name || '',
      place: i + 1,
      winnings: payoutAmounts[i] ?? 0,
      totalPaid,
      durationSeconds,
      rebuys: p.rebuys || 0,
      addOn: !!p.addOn,
      knockedOutBy: '',
      ...(p.linkedUserAlias && { linkedUserAlias: p.linkedUserAlias }),
      ...(p.linkedUserId && { linkedUserId: p.linkedUserId })
    });
  }
  for (let i = 0; i < bustedSorted.length; i++) {
    const { playerId, tournamentSecondsAtBust: exitTournamentSeconds, knockedOutBy } = bustedSorted[i];
    const p = players.value.find(pl => String(pl.id) === playerId);
    if (!p) continue;
    const totalPaid = buyinAmount.value + (p.rebuys || 0) * rebuyAmount.value + (p.addOn ? addonAmount.value : 0);
    const place = N - i;
    const exitSeconds = typeof exitTournamentSeconds === 'number' ? exitTournamentSeconds : currentTournamentSeconds;
    const durationSeconds = exitSeconds;
    playerResults.push({
      name: p.name || '',
      place,
      winnings: payoutAmounts[place - 1] ?? 0,
      totalPaid,
      durationSeconds,
      rebuys: p.rebuys || 0,
      addOn: !!p.addOn,
      knockedOutBy: knockedOutBy || '',
      ...(p.linkedUserAlias && { linkedUserAlias: p.linkedUserAlias }),
      ...(p.linkedUserId && { linkedUserId: p.linkedUserId })
    });
  }

  const savedPayouts = (payoutSource || []).map((p, i) => ({ position: p?.position ?? i + 1, amount: p?.amount ?? 0 }));

  const payload = {
    tournamentName: tournamentNameToEnd,
    createdBy: ownerId,
    endedAt,
    buyInAmount: buyinAmount.value,
    rebuyAmount: rebuyAmount.value,
    addonAmount: addonAmount.value,
    totalPrizePool: totalPot.value,
    levels: levels.value || [],
    payouts: savedPayouts,
    players: playerResults,
    leagueId: tournamentStore.settings.leagueId || null,
  };

  const col = collection(db, SAVED_TOURNAMENT_RESULTS_DB);
  const resultRef = await addDoc(col, payload);
  const resultId = resultRef.id;

  // Total entries = unique players + all rebuys
  const totalEntries = players.value.reduce((sum, p) => sum + 1 + (p.rebuys || 0), 0);

  // Phase 3 / 4.4: For each linked player, create a session only if alias is in current user's allowed list (security)
  const sessionsCol = collection(db, SESSIONS_DB);
  const runnerUid = userStore.userid;
  const allowedPlayerOptions = runnerUid ? await getAllowedPlayerOptions(runnerUid) : [];
  const allowedAliases = new Set(allowedPlayerOptions.map(o => o.alias));
  // Runner can always create a session for themselves
  if (userStore.username) allowedAliases.add(userStore.username);
  console.log('[saveTournamentResults] runnerUid:', runnerUid);
  console.log('[saveTournamentResults] allowedAliases:', [...allowedAliases]);
  console.log('[saveTournamentResults] playerResults with linked:', playerResults.map(p => ({ name: p.name, place: p.place, linkedUserAlias: p.linkedUserAlias })));
  for (const player of playerResults) {
    const alias = player.linkedUserAlias;
    console.log(`[saveTournamentResults] Processing player: ${player.name}, alias: ${alias}, inAllowed: ${alias ? allowedAliases.has(alias) : 'n/a'}`);
    if (!alias) continue;
    if (!allowedAliases.has(alias)) continue;
    try {
      const existingQ = query(
        sessionsCol,
        where('user_id', '==', player.linkedUserId),
        where('tournamentResultId', '==', resultId)
      );
      const existingSnap = await getDocs(existingQ);
      if (!existingSnap.empty) { console.log(`[saveTournamentResults] Session already exists for ${alias}, skipping`); continue; }
      const winnings = Number(player.winnings) ?? 0;
      const totalPaid = Number(player.totalPaid) ?? 0;
      const sessionDoc = {
        from: alias,
        user_id: player.linkedUserId,
        sessiondate: new Date(endedAt),
        place: '',
        game: '',
        length: player.durationSeconds / 3600,
        buyin: totalPaid,
        rebuys: player.rebuys ?? 0,
        players: totalEntries,
        finishedin: player.place ?? 0,
        winnings,
        bettype: NO_LIMIT,
        bigblind: null,
        net: winnings - totalPaid,
        type: TOURNAMENT,
        notes: `From Tournament Timer: ${tournamentNameToEnd}`,
        tournamentResultId: resultId
      };
      console.log(`[saveTournamentResults] Writing session doc for ${alias}:`, sessionDoc);
      const newDocRef = await addDoc(sessionsCol, sessionDoc);
      console.log(`[saveTournamentResults] Session created for ${alias}, docId: ${newDocRef.id}`);
    } catch (e) {
      console.error('Error syncing session for linked player:', alias, e);
    }
  }
}

// End current tournament and return to create screen
async function endTournament() {
  if (!newTournamentName.value) return;
  
  const tournamentNameToEnd = newTournamentName.value;
  
  try {
    const settingsDocRef = doc(db, TOURNAMENT_SETTINGS_DB, tournamentNameToEnd);
    const playersDocRef = doc(db, TOURNAMENT_PLAYERS_DB, tournamentNameToEnd);
    const payoutsDocRef = doc(db, TOURNAMENT_PAYOUTS_DB, tournamentNameToEnd);
    const timersDocRef = doc(db, TOURNAMENT_TIMERS_DB, tournamentNameToEnd);

    // Delete tournament docs from all four collections when tournament is ended
    await Promise.all([
      deleteDoc(settingsDocRef),
      deleteDoc(playersDocRef),
      deleteDoc(payoutsDocRef),
      deleteDoc(timersDocRef)
    ]);
    setFeedbackMessage('Tournament ended and data deleted successfully.');

    // Reset local state
    tournamentActive.value = false;
    timeLeft.value = null;
    countdownDuration.value = null;
    level.value = 1;
    normalLevelCount.value = 1;
    timerRunning.value = false;
    players.value = [];
    bustedPlayers.value = [];
    payouts.value = [];
    levels.value = [];
    chipDenominations.value = [];
    newTournamentName.value = '';
    tournamentStore.setTournamentName(''); // Also clear from store
    shareId.value = '';
    
    // Stop timer if running
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    
    setFeedbackMessage('Tournament ended successfully.');
    
    // Close the confirmation dialog
    showEndTournamentDialog.value = false;
    
  } catch (e) {
    console.error('Error ending tournament:', e);
    setFeedbackMessage('Error ending tournament. Please try again.', true);
  }
}

// Helper function to set feedback message with auto-clear
function setFeedbackMessage(message) {
  feedback.value = message;
  // Clear feedback after 5 seconds
  setTimeout(() => {
    if (feedback.value === message) {
      feedback.value = '';
    }
  }, 5000);
}

async function adjustPlayerCount(type, amount) {
  switch (type) {
    case 'totalPlayers':
      manualTotalPlayers.value = Math.max(0, manualTotalPlayers.value + amount);
      break;
    case 'totalRebuys':
      manualTotalRebuys.value = Math.max(0, manualTotalRebuys.value + amount);
      break;
    case 'totalAddOns':
      manualTotalAddOns.value = Math.max(0, manualTotalAddOns.value + amount);
      break;
    case 'bustedPlayers':
      bustedPlayerCount.value = Math.max(0, bustedPlayerCount.value + amount);
      break;
  }
  // Save the updated counts to Firestore.
  await savePlayerCounts();

  // After updating counts, recalculate payouts
  if (type === 'totalPlayers' || type === 'totalRebuys') {
    const { recalculateAndSavePayouts } = usePayouts();

    // Recalculate total pot using the *updated* manual counts (rake deducted per entry)
    let currentTotalPot = manualTotalPlayers.value * (buyinAmount.value - buyinRake.value);
    if (rebuyAllowed.value) {
      currentTotalPot += manualTotalRebuys.value * (rebuyAmount.value - rebuyRake.value);
    }
    if (addonAllowed.value) {
      currentTotalPot += manualTotalAddOns.value * (addonAmount.value - addonRake.value);
    }

    await recalculateAndSavePayouts({
      userId: user.value.uid,
      tournamentId: newTournamentName.value,
      numPlayers: manualTotalPlayers.value,
      numRebuys: manualTotalRebuys.value,
      totalPot: currentTotalPot,
      payoutTables: payoutTables.value
    });
  }
}

async function savePlayerCounts() {
  if (!newTournamentName.value || trackPlayersByName.value) return;

  const playersDocRef = doc(db, TOURNAMENT_PLAYERS_DB, newTournamentName.value);
  try {
    await updateDoc(playersDocRef, {
      totalPlayers: manualTotalPlayers.value,
      totalRebuys: manualTotalRebuys.value,
      totalAddOns: manualTotalAddOns.value,
      bustedPlayerCount: bustedPlayerCount.value,
    });
  } catch (e) {
    console.error('Error saving manual player counts:', e);
  }
}

async function loadAllTournamentData() {
  if (!tournamentActive.value || !newTournamentName.value) return;
  
  setupPlayersListener();
  await loadTournamentSettings();
  await loadLevelsFromSettings();
  await loadDefaultDurationFromSettings();
  await loadStateFromFirestore();
  setupPayoutsListener();
  setupSettingsListener();
  // Ensure payouts are correct after loading settings/players
  await ensurePayoutsUpToDate();
  updateNormalLevelCount();
  if (timerRunning.value && !timerInterval) {
    if (playersLeft.value >= 2) {
      console.log('Timer was running, restarting timer.');
      startTimer();
    } else {
      timerRunning.value = false;
      saveStateToFirestore();
    }
  }
}

// Ensure payouts document reflects the current total pot/players.
async function ensurePayoutsUpToDate() {
  if (!newTournamentName.value) return;
  // If there's nothing to pay out yet, skip recalculation
  if (totalPot.value <= 0) return;

  // If payouts array is empty or sums to zero, re-run initialization
  const currentSum = (payouts.value || []).reduce((s, p) => s + (p?.amount || 0), 0);
  if (!payouts.value || payouts.value.length === 0 || currentSum === 0) {
    try {
      await initializePayoutsFromTemplate();
    } catch (e) {
      console.error('Error ensuring payouts up to date:', e);
    }
  }
}

function setupPayoutsListener() {
  if (payoutsUnsub) {
    payoutsUnsub();
  }
  if (!newTournamentName.value) {
    //console.log('No tournament name for payouts listener');
    payouts.value = [];
    numberOfPayouts.value = 0;
    return;
  }

  const payoutsDoc = doc(db, TOURNAMENT_PAYOUTS_DB, newTournamentName.value);
  payoutsUnsub = onSnapshot(payoutsDoc, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      numberOfPayouts.value = data.numberOfPayouts || 0;
      payouts.value = data.payouts || [];
    } else {
      // If no payouts exist, wait for player data to be available, then initialize.
      // This prevents a race condition on initial load.
      const unwatch = watch(players, () => {
        initializePayoutsFromTemplate();
        unwatch(); // Stop watching after initialization
      }, { deep: true });
    }
  });
}

function setupSettingsListener() {
  if (settingsUnsub) {
    settingsUnsub();
  }
  if (!newTournamentName.value) {
    displayConfig.value = mergeDisplayConfig();
    return;
  }

  const settingsDocRef = doc(db, TOURNAMENT_SETTINGS_DB, newTournamentName.value);
  settingsUnsub = onSnapshot(settingsDocRef, (docSnap) => {
    if (!docSnap.exists()) return;
    const data = docSnap.data();
    displayConfig.value = mergeDisplayConfig(data.displayConfig);
    postedAnnouncementText.value = typeof data.announcement?.text === 'string' ? data.announcement.text : '';
  });
}

// New function to initialize payouts from template
async function initializePayoutsFromTemplate() {
  if (!newTournamentName.value) {
    setFeedbackMessage('No tournament name set. Cannot load payouts.', true);
    return;
  }
  // Get players from Firestore
  const playersDoc = doc(db, TOURNAMENT_PLAYERS_DB, newTournamentName.value);
  const playersSnap = await getDoc(playersDoc);
  let playersArr = [];
  if (playersSnap.exists()) {
    const data = playersSnap.data();
    playersArr = data.players || [];
  }
  
  // Use the payout tables to initialize payouts
  const { recalculateAndSavePayouts } = usePayouts();

  const numPlayers = trackPlayersByName.value
    ? playersArr.length
    : manualTotalPlayers.value;
  const numRebuys = trackPlayersByName.value
    ? playersArr.reduce((acc, p) => acc + (p.rebuys || 0), 0)
    : manualTotalRebuys.value;

  await recalculateAndSavePayouts({
    userId: user.value.uid,
    tournamentId: newTournamentName.value,
    numPlayers,
    numRebuys,
    totalPot: totalPot.value,
    payoutTables: payoutTables.value
  });
  
  // Reload payouts after initialization
  // The listener will automatically pick up the changes.
}

onUnmounted(() => {
  stopTimer({ keepRunningFlag: true });
  if (playersUnsub) {
    playersUnsub();
  }
  if (payoutsUnsub) {
    payoutsUnsub();
  }
  if (settingsUnsub) {
    settingsUnsub();
  }
  // Release any wake lock on unmount
  try { releaseWakeLock(); } catch (e) { /* ignore */ }
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

watch(user, async (newUser) => {
  console.log('User watcher triggered. New user:', newUser);
  if (timerInterval) {
    stopTimer({ keepRunningFlag: true });
  }

  if (playersUnsub) {
    playersUnsub();
    playersUnsub = null;
  }

  if (payoutsUnsub) {
    payoutsUnsub();
    payoutsUnsub = null;
  }

  if (settingsUnsub) {
    settingsUnsub();
    settingsUnsub = null;
  }

  if (newUser && newUser.uid) {
    //console.log('User has UID, loading data...');
    
    // Load structures first for the create dialog
    await loadStructureOptions();
    
    // Check if there's an existing tournament
    await checkTournamentStatus();
    if (tournamentActive.value) { await loadAllTournamentData(); }
  } else {
    //console.log('No user or UID, resetting state.');
    // Reset state if user logs out
    tournamentActive.value = false;
    timeLeft.value = null;
    countdownDuration.value = null;
    level.value = 1;
    normalLevelCount.value = 1;
    timerRunning.value = false;
    players.value = [];
    bustedPlayers.value = [];
    manualTotalPlayers.value = 0;
    manualTotalRebuys.value = 0;
    manualTotalAddOns.value = 0;
    bustedPlayerCount.value = 0;
    payouts.value = [];
    levels.value = [];
    manualTotalPlayers.value = 0;
    manualTotalRebuys.value = 0;
    manualTotalAddOns.value = 0;
    bustedPlayerCount.value = 0;
    chipDenominations.value = [];
    newTournamentName.value = '';
    shareId.value = '';
  }
}, { deep: true });

// Check if there's an existing tournament
async function checkTournamentStatus() {
  if (!user.value?.uid) {
    tournamentActive.value = false;
    return;
  }

  try {
    const settingsCol = collection(db, TOURNAMENT_SETTINGS_DB);
    const q = query(settingsCol, where('ownerId', '==', user.value.uid));
    const querySnapshot = await getDocs(q);

    const activeTournaments = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      // An active tournament is one that has at least one level defined.
      if (data.levels && Array.isArray(data.levels) && data.levels.length > 0) {
        activeTournaments.push({ id: doc.id, ...data });
      }
    });

    activeTournamentsForUser.value = activeTournaments;

    if (activeTournaments.length === 1) {
      // If there's only one active tournament, load it automatically.
      await loadSelectedTournament(activeTournaments[0].id);
    } else if (activeTournaments.length > 1) {
      // If there are multiple, show the selection dialog.
      showSelectTournamentDialog.value = true;
    } else {
      // If there are no active tournaments, show the create screen.
      tournamentActive.value = false;
    }
  } catch (e) {
    console.error('Error checking tournament status:', e);
    feedback.value = 'Error checking for active tournaments.';
    tournamentActive.value = false;
  }
}

async function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    if (tournamentActive.value) {
      console.log('Tab is visible again, reloading all tournament data.');
      await loadAllTournamentData();
    }
    // Re-acquire wake lock when the page becomes visible again
    if (tournamentActive.value && timerRunning.value) {
      await requestWakeLock();
    }
  } else {
    // Page hidden: release any wake lock to be a good citizen
    await releaseWakeLock();
    // Save current timer state so that the reload on becoming-visible reads fresh data.
    if (tournamentActive.value && timerRunning.value) {
      await saveStateToFirestore();
    }
  }
}

onMounted(async () => {
  await checkTournamentStatus();
  if (tournamentActive.value) { 
   /* if (process.env.NODE_ENV === 'development') {
      setupCustomDevtools(router.app, {
        getTimerState: () => ({
          timeLeft,
          endTime,
          timerRunning,
        }),
      });
    }*/
    console.log('OnMounted: Tournament is active, loading all tournament data.');
    await loadAllTournamentData(); 

  }
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

// Add a click handler to validate `newTournamentName` before navigating
function navigateToPlayersView() {
  if (!newTournamentName.value) {
    feedback.value = 'Error: Tournament name is not defined. Please create or select a tournament.';
    console.error('Error: Tournament name is not defined.');
    return;
  }
  tournamentStore.setTournamentName(newTournamentName.value);
  // Ensure the store has the latest settings before navigating
  tournamentStore.settings = {
    buyinAmount: buyinAmount.value,
    rebuyAmount: rebuyAmount.value,
    addonAmount: addonAmount.value,
      maxTables: maxTables.value,
      maxPlayersPerTable: maxPlayersPerTable.value,
    rebuyAllowed: rebuyAllowed.value,
    rebuyEndLevel: rebuyEndLevel.value,
    addonAllowed: addonAllowed.value,
    payoutTables: payoutTables.value,
    trackPlayersByName: trackPlayersByName.value,
  };
  router.push({ name: 'PlayersView' });
}

</script>

<style scoped>
.payout-scroll-move, .payout-scroll-enter-active, .payout-scroll-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.payout-scroll-enter-from, .payout-scroll-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.payout-scroll-leave-active {
  position: absolute;
}
.tournament-main-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 32px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 24px auto;
}
.tournament-stats-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 32px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 8px auto;
  font-size: 1rem;
  color: #888;
  text-align: center;
}
/* Removed empty ruleset for .tournament-main-row > div, .tournament-stats-row > .stats-col */
.tournament-main-row > div:first-child,
.tournament-main-row > div:last-child {
  text-align: center;
}
.tournament-main-row > div:first-child,
.tournament-stats-row > .stats-col-left {
  flex-basis: 260px;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: 220px;
  max-width: 260px;
  text-align: left;
  margin-right: 0;
}
.tournament-main-row > div:last-child,
.tournament-stats-row > .stats-col-right {
  flex-basis: 260px;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: 220px;
  max-width: 260px;
  text-align: right;
  margin-left: 0;
}
.tournament-main-row > div:nth-child(2),
.tournament-stats-row > .stats-col-center {
  flex: 1 1 0;
  min-width: 160px;
  text-align: center;
}
.tournament-stats-row > .stats-col-left,
.tournament-stats-row > .stats-col-right {
  text-align: center;
}
.main-col-left,
.main-col-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}


.tournament-stats-row > .stats-col-left,
.tournament-stats-row > .stats-col-right {
  text-align: center;
}
.main-col-left,
.main-col-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.tournament-stats-row > .stats-col-left {
  flex-basis: 260px;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: 220px;
  max-width: 260px;
  text-align: left;
  margin-right: 0;
}
.tournament-main-row > div:last-child,
.tournament-stats-row > .stats-col-right {
  flex-basis: 260px;
  flex-shrink: 0;
  flex-grow: 0;
  min-width: 220px;
  max-width: 260px;
  text-align: right;
  margin-left: 0;
}
.tournament-main-row > div:nth-child(2),
.tournament-stats-row > .stats-col-center {
  flex: 1 1 0;
  min-width: 160px;
  text-align: center;
}
.tournament-stats-row > .stats-col-left,
.tournament-stats-row > .stats-col-right {
  text-align: center;
}
.main-col-left,
.main-col-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}


.tournament-stats-row > .stats-col-left,
.tournament-stats-row > .stats-col-right {
  text-align: center;
}
.main-col-left,
.main-col-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.control-buttons {
  margin-bottom: 24px;
}

/* Tablet landscape optimizations for the tournament view */
@media (min-width: 768px) and (orientation: landscape) {
  .control-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
  }
  .control-buttons .v-btn {
    padding: 10px 18px;
    font-size: 1rem;
  }
  h1 {
    font-size: 1.5rem;
  }
  .tournament-main-row {
    gap: 24px;
    max-width: 1400px;
  }
  .chip-display-container {
    max-width: 1000px;
  }
}

@media (min-width: 1000px) and (orientation: landscape) {
  .control-buttons .v-btn {
    padding: 12px 22px;
    font-size: 1.05rem;
  }
  h1 { font-size: 1.75rem; }
  .tournament-main-row { gap: 32px; }
}

/* Force-tablet preview (admin toggle) - use deep selectors for child components */
.force-tablet {
  /* make root spacing similar to tablet landscape */
  /* Non-intrusive flag to avoid empty-block lint warnings */
  --force-tablet-active: 1;
}
.force-tablet .control-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}
.force-tablet .control-buttons .v-btn { padding: 10px 18px; font-size: 1rem; }
.force-tablet h1 { font-size: 1.5rem; }
.force-tablet .chip-display-container { max-width: 1000px; }

.force-tablet :deep(.tournament-main-row) {
  gap: 20px !important;
  max-width: 1200px !important;
  align-items: center !important;
}
.force-tablet :deep(.countdown-timer) {
  font-size: 7.5rem !important;
  line-height: 1 !important;
}
.force-tablet :deep(.main-col-left),
.force-tablet :deep(.main-col-right) {
  flex-basis: 220px !important;
  min-width: 180px !important;
}
.force-tablet :deep(.small-blind),
.force-tablet :deep(.big-blind),
.force-tablet :deep(.ante-value) {
  font-size: 4.8rem !important;
}
.force-tablet :deep(.blinds-container) { font-size: 2.8rem !important; }
.force-tablet :deep(.payouts-title) { font-size: 1.1rem !important; }


/* Mobile: stack columns vertically on phones */
@media (max-width: 599px) {
  /* Pull nav buttons out of absolute positioning so title gets its own row */
  .tournament-nav-buttons {
    position: static !important;
    justify-content: flex-end;
    margin-bottom: 4px;
  }

  .tournament-main-row,
  .tournament-stats-row {
    flex-direction: column !important;
    gap: 8px !important;
    align-items: center !important;
  }
  .tournament-main-row > div,
  .tournament-stats-row > div {
    flex-basis: auto !important;
    max-width: 100% !important;
    min-width: 0 !important;
    width: 100% !important;
    text-align: center !important;
  }
}

.chip-visual {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.2rem;
  border: 8px dashed;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2), inset 0 2px 4px rgba(255,255,255,0.5);
  text-shadow: 0 1px 1px rgba(0,0,0,0.2);
}

.next-break-notice {
  text-align: center;
  font-weight: 600;
  color: #555;
  margin: 24px auto 0;
}

</style>
